import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
	AlertTriangle,
	Box,
	Check,
	CheckCircle2,
	ImageIcon,
	Info,
	Pencil,
	X,
	XCircle,
	type LucideIcon,
} from "lucide-react";
import { Layout } from "../layout/Layout";
import defaultAvatarUrl from "../assets/default-avatar.svg";
import { ProfesionalCard } from "../components/Profesional/ProfesionalCard";
import { ProfesionalDetails } from "../components/Profesional/ProfesionalDetails";
import { useAuthSessionContext } from "../components/routing/auth-session-store";
import { getImageUri } from "../core/utils/imageLoad";
import { patchFormData, postFormData, requestApi } from "../core/api/api";
import { ModalAddService } from "../components/modalAddService";
import { ModalImagenesCompletado } from "../components/modals/ModalImagenesCompletado";
import { useCategories } from "../hooks/category/useCategories";
import { useProviderServiceCategories } from "../hooks/services/useProviderServiceCategories";
import { useToggleServiceActive } from "../hooks/services/useServicesProvider";
import { useFetch } from "../hooks/useFetch";
import { useCreateOrder, useProviderOrders } from "../hooks/services/useOrders";
import { useCreatePortfolio } from "../hooks/services/usePortfolio";
import { useCreateSchedule } from "../hooks/services/useSchedules";
import {
	useAccountsByProvider,
	useCreateAccount,
	useDeleteAccount,
	useUpdateAccount,
} from "../hooks/services/useAccount";
import {
	useCreateFaq,
	useDeleteFaq,
	useFaqsByProvider,
	useUpdateFaq,
} from "../hooks/services/useFAQ";
import { useAssignedBadges } from "../hooks/control/useBadge";
import { useMethods } from "../hooks/control/useMethods";
import { createDefaultAvailability } from "../core/utils/provider-registration";
import {
	clearPendingProviderCategories,
	getPendingProviderCategories,
} from "../core/registration/provider-categories-pending";
import {
	clearPendingProviderSchedule,
	getPendingProviderSchedule,
} from "../core/registration/provider-schedule-pending";
import { getPhoneValidationError } from "../core/utils/phone";
import { resolveProviderReviewsCount } from "../core/utils/providerStats";
import type { DayAvailability } from "../core/types/provider-registration";
import type {
	AccountResponse,
	CreateAccountPayload,
	CreateFaqPayload,
	FaqResponse,
	UpdateAccountPayload,
	UpdateFaqPayload,
} from "../core/types/services";
import type { BadgeAssignmentResponse, MethodResponse } from "../core/types/control";
import type {
	FaqCategory,
	FaqRow,
	ObtainedBadge,
	OrderRow,
	PaymentMethodRow,
	PaymentMethodSavePayload,
	ProfessionalPersonalInfo,
	ProfessionalStats,
	ProviderAddressRow,
} from "../core/types/tsx/components/Profesional/ProfesionalDetails.types";
import type {
	ApiAddress,
	ApiService,
	AddressesByUserResponse,
	CategorieProviderRelation,
	ServicesByProviderResponse,
	ServiceRow,
	ApiProvider,
	ApiSchedule,
	SchedulesByProviderResponse,
} from "../core/types/tsx/pages/ServiceProfile.types";
import type { PaginatedResponse, OrderResponse } from "../core/types/services";

const formatPrice = (value?: number | null) => {
	if (value === null || value === undefined || Number.isNaN(value)) return "-";
	return new Intl.NumberFormat("es-VE", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 2,
	}).format(value);
};

const toArray = <T,>(value: T | T[] | null | undefined): T[] => {
	if (Array.isArray(value)) return value;
	if (value === null || value === undefined) return [];
	return [value];
};

const resolveServiceCategoryName = (
	service: NonNullable<ServicesByProviderResponse["services"]>[number],
) => {
	const currentRelations = toArray(
		(
			service as {
				categories_services_providers?:
					| NonNullable<ApiService["categories_services_providers"]>
					| NonNullable<ApiService["categories_services_providers"]>[number]
					| null;
			}
		).categories_services_providers,
	);

	for (const relation of currentRelations) {
		const name = relation?.categories_providers?.categories_services?.name_category;
		if (typeof name === "string" && name.trim()) {
			return name.trim();
		}
	}

	const legacyRelations = toArray(
		(
			service as {
				categoriesServices_servicesProviders?:
					| NonNullable<ApiService["categoriesServices_servicesProviders"]>
					| NonNullable<
							ApiService["categoriesServices_servicesProviders"]
					  >[number]
					| null;
			}
		).categoriesServices_servicesProviders,
	);

	for (const relation of legacyRelations) {
		const name = relation?.categoriesServices?.name_category;
		if (typeof name === "string" && name.trim()) {
			return name.trim();
		}
	}

	return "-";
};

const resolveServiceSubcategoryNames = (
	service: NonNullable<ServicesByProviderResponse["services"]>[number],
) => {
	const currentRelations = toArray(
		(
			service as {
				categories_services_providers?:
					| NonNullable<ApiService["categories_services_providers"]>
					| NonNullable<ApiService["categories_services_providers"]>[number]
					| null;
			}
		).categories_services_providers,
	);

	const selectedNames = currentRelations.flatMap((relation) =>
		toArray(relation?.subcategories_services_providers)
			.map((item) => item?.subcategories_services?.name_subcategory?.trim())
			.filter((name): name is string => Boolean(name)),
	);

	if (selectedNames.length > 0) {
		return Array.from(new Set(selectedNames)).join(", ");
	}

	const fallbackNames = currentRelations.flatMap((relation) =>
		toArray(
			relation?.categories_providers?.categories_services?.subcategories_services,
		)
			.map((item) => item?.name_subcategory?.trim())
			.filter((name): name is string => Boolean(name)),
	);

	if (fallbackNames.length > 0) {
		return Array.from(new Set(fallbackNames)).join(", ");
	}

	return service.name_service || "Servicio";
};

const extractSelectedSubcategoryNames = (
	service: NonNullable<ServicesByProviderResponse["services"]>[number],
) => {
	const currentRelations = toArray(
		(
			service as {
				categories_services_providers?:
					| NonNullable<ApiService["categories_services_providers"]>
					| NonNullable<ApiService["categories_services_providers"]>[number]
					| null;
			}
		).categories_services_providers,
	);

	return Array.from(
		new Set(
			currentRelations
				.flatMap((relation) =>
					toArray(relation?.subcategories_services_providers).map((item) =>
						item?.subcategories_services?.name_subcategory?.trim(),
					),
				)
				.filter((name): name is string => Boolean(name)),
		),
	);
};

const normalizeLookupKey = (value: string) =>
	value
		.toLowerCase()
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "")
		.replace(/[^a-z0-9\s]/g, "")
		.replace(/\s+/g, " ")
		.trim();

const parseMultilineTools = (value?: string | null) =>
	(value ?? "")
		.split("\n")
		.map((item) => item.trim())
		.filter((item) => item.length > 0);

const extractServices = (payload: unknown) => {
	if (Array.isArray(payload)) {
		return payload as NonNullable<ServicesByProviderResponse["services"]>;
	}
	if (!payload || typeof payload !== "object") return [];
	const wrapped = payload as ServicesByProviderResponse & {
		items?: NonNullable<ServicesByProviderResponse["services"]>;
		data?:
			| NonNullable<ServicesByProviderResponse["services"]>
			| ServicesByProviderResponse
			| {
					items?: NonNullable<ServicesByProviderResponse["services"]>;
			  }
			| null;
	};
	if (Array.isArray(wrapped.services)) return wrapped.services;
	if (Array.isArray(wrapped.items)) return wrapped.items;
	if (Array.isArray(wrapped.data)) return wrapped.data;
	if (wrapped.data && typeof wrapped.data === "object") {
		const nested = wrapped.data as ServicesByProviderResponse & {
			items?: NonNullable<ServicesByProviderResponse["services"]>;
		};
		if (Array.isArray(nested.services)) return nested.services;
		if (Array.isArray(nested.items)) return nested.items;
	}
	return [];
};

const extractCategoryProviderRelations = (
	payload: unknown,
): CategorieProviderRelation[] => {
	if (Array.isArray(payload)) {
		return payload as CategorieProviderRelation[];
	}

	if (!payload || typeof payload !== "object") return [];

	const wrapped = payload as {
		"categorie-providers"?: CategorieProviderRelation[];
		items?: CategorieProviderRelation[];
	};

	if (Array.isArray(wrapped["categorie-providers"])) {
		return wrapped["categorie-providers"];
	}

	if (Array.isArray(wrapped.items)) {
		return wrapped.items;
	}

	return [];
};

const DEFAULT_PROFILE_AVATAR = defaultAvatarUrl;
const NO_EXPERIENCE_MESSAGE = "Aún no has colocado tus años de experiencia";

const resolveProfileAvatar = (...candidates: Array<string | null | undefined>) => {
	for (const candidate of candidates) {
		const resolved = getImageUri(candidate);
		if (resolved) return resolved;
	}

	return DEFAULT_PROFILE_AVATAR;
};

const mapFaqsToRows = (faqs: FaqResponse[] = []): FaqRow[] =>
	faqs
		.filter((faq) => faq.is_active !== false)
		.map((faq) => ({
			id: faq.id,
			categoryId: faq.category_id,
			question: faq.question,
			answer: faq.answer,
			orderDisplay: faq.order_display ?? 0,
			isActive: faq.is_active ?? true,
		}))
		.sort((a, b) => (a.orderDisplay ?? 0) - (b.orderDisplay ?? 0));

const extractSchedules = (payload: unknown): ApiSchedule[] => {
	if (Array.isArray(payload)) return payload as ApiSchedule[];
	if (!payload || typeof payload !== "object") return [];
	const wrapped = payload as SchedulesByProviderResponse & { items?: ApiSchedule[] };
	if (Array.isArray(wrapped.schedules)) return wrapped.schedules;
	if (Array.isArray(wrapped.items)) return wrapped.items;
	return [];
};

const extractAddresses = (payload: unknown): ApiAddress[] => {
	if (Array.isArray(payload)) return payload as ApiAddress[];
	if (!payload || typeof payload !== "object") return [];
	const wrapped = payload as AddressesByUserResponse & { items?: ApiAddress[] };
	if (Array.isArray(wrapped.addresses)) return wrapped.addresses;
	if (Array.isArray(wrapped.items)) return wrapped.items;
	return [];
};

const extractAddressesFromUserPayload = (payload: unknown): ApiAddress[] => {
	if (!payload || typeof payload !== "object") return [];
	const wrapped = payload as {
		addresses?: ApiAddress[];
		user?: { addresses?: ApiAddress[] } | null;
	};

	if (Array.isArray(wrapped.addresses)) return wrapped.addresses;
	if (Array.isArray(wrapped.user?.addresses)) return wrapped.user.addresses;
	return [];
};

const mapProviderAddressesToRows = (addresses: ApiAddress[] = []): ProviderAddressRow[] =>
	addresses
		.map((address) => {
			const id =
				typeof address.id === "number" && Number.isFinite(address.id)
					? address.id
					: null;
			if (!id) return null;

			const state =
				address.name_state?.trim() ||
				address.states?.name_state?.trim() ||
				address.states?.name_complete?.trim() ||
				"";
			const city =
				address.name_city?.trim() ||
				address.municipalities?.name_municipality?.trim() ||
				address.municipalities?.name_complete?.trim() ||
				address.parishes?.name_parish?.trim() ||
				"";

			if (!state && !city) return null;

			return {
				id,
				state: state || "-",
				city: city || "-",
				isActive: address.is_active !== false,
			};
		})
		.filter((item): item is ProviderAddressRow => Boolean(item));

const normalizeMethodName = (value?: string | null) =>
	(value ?? "")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/\s+/g, "")
		.trim();

const mapMethodNameToUiType = (
	methodName?: string | null,
): PaymentMethodSavePayload["methodType"] | null => {
	const normalized = normalizeMethodName(methodName);
	if (!normalized) return null;

	if (normalized.includes("transferencia")) {
		return "transferencia_bancaria";
	}

	if (normalized.includes("pagomovil") || normalized.includes("movil")) {
		return "pago_movil";
	}

	return null;
};

const resolveAccountMethodType = (
	account: AccountResponse,
	methodsCatalog: MethodResponse[] = [],
): PaymentMethodSavePayload["methodType"] | null => {
	const fromRelation = mapMethodNameToUiType(account.methods?.name_method);
	if (fromRelation) return fromRelation;

	const fromMethodId = methodsCatalog.find((method) => method.id === account.method_id);
	const fromMethodIdName = mapMethodNameToUiType(fromMethodId?.name_method);
	if (fromMethodIdName) return fromMethodIdName;

	return mapMethodNameToUiType(account.methods_payment);
};

const resolveMethodIdByUiType = (
	methodType: PaymentMethodSavePayload["methodType"],
	methodsCatalog: MethodResponse[] = [],
) => {
	const match = methodsCatalog.find((method) => {
		const candidateType = mapMethodNameToUiType(method.name_method);
		return candidateType === methodType;
	});

	return match?.id ?? null;
};

const mapProviderAccountsToPaymentRows = (
	accounts: AccountResponse[] = [],
	methodsCatalog: MethodResponse[] = [],
	holderName?: string,
): PaymentMethodRow[] => {
	const safeHolderName = holderName?.trim() || "Titular de la cuenta";
	const rows = accounts
		.map((account) => {
			const methodType = resolveAccountMethodType(account, methodsCatalog);
			if (!methodType) return null;

			const methodId =
				typeof account.method_id === "number"
					? account.method_id
					: typeof account.methods?.id === "number"
						? account.methods.id
						: resolveMethodIdByUiType(methodType, methodsCatalog);

			if (!methodId) return null;

			const bankName = account.name_bank?.trim() || "Banco";
			const accountNumber = account.number_account?.trim() || "";
			const phoneNumber =
				account.number_phone?.trim() ||
				(methodType === "pago_movil" ? accountNumber : "");

			const row: PaymentMethodRow = {
				id: account.id,
				methodId,
				methodType,
				bankName,
				accountNumber:
					methodType === "transferencia_bancaria" ? accountNumber : "",
				phoneNumber: methodType === "pago_movil" ? phoneNumber : "",
				identification: account.identification?.trim() || "",
				holderName: safeHolderName,
				isDefault: Boolean(account.is_primary),
			};

			return row;
		})
		.filter((item): item is PaymentMethodRow => item !== null);

	return rows.sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
};

// ── Order types & helpers (module-level) ──

type RawOrdersResponse = PaginatedResponse<OrderResponse> & {
	orders?: OrderResponse[];
};

type StructuredOrderRequestDescription = {
	format?: string;
	urgency?: string;
	category?: string;
	subcategory?: string[];
	items?: Array<{ lineTotal?: number }>;
	total?: number;
};

type ProviderStatusLabel =
	| "Completado"
	| "En Proceso"
	| "Dialogando"
	| "Cancelado"
	| "En Espera"
	| "Pagado"
	| "Garantía"
	| "Soporte";

const parseOrderRequestDescription = (
	raw?: string | null,
): StructuredOrderRequestDescription | null => {
	if (!raw) return null;
	const marker = "SERVIX_REQUEST_V1::";
	if (!raw.startsWith(marker)) return null;
	const encoded = raw.slice(marker.length).trim();
	if (!encoded) return null;
	try {
		const parsed = JSON.parse(encoded) as StructuredOrderRequestDescription;
		return parsed?.format === "SERVIX_REQUEST_V1" ? parsed : null;
	} catch {
		return null;
	}
};

const mapBackendStatusToUi = (status?: string | null): ProviderStatusLabel => {
	const normalized = String(status ?? "")
		.toLowerCase()
		.trim();
	if (normalized.includes("cancel")) return "Cancelado";
	if (normalized.includes("pagad")) return "Pagado";
	if (normalized.includes("complet")) return "Completado";
	if (normalized.includes("garantia") || normalized.includes("garantía"))
		return "Garantía";
	if (normalized.includes("soporte")) return "Soporte";
	if (normalized.includes("esper")) return "En Espera";
	if (normalized.includes("dialog") || normalized.includes("pend")) return "Dialogando";
	return "En Proceso";
};

const extractApiOrders = (payload: RawOrdersResponse | null) =>
	Array.isArray(payload?.items)
		? payload.items
		: Array.isArray(payload?.orders)
			? payload.orders
			: [];

type RequestServiceCategorySource =
	| {
			categories_services_providers?: Array<{
				categories_providers?: {
					categories_services?: {
						name_category?: string | null;
					} | null;
				} | null;
			}> | null;
			categoriesServices_servicesProviders?: Array<{
				categoriesServices?: {
					name_category?: string | null;
				} | null;
			}> | null;
	  }
	| null
	| undefined;

const resolveRequestServiceCategory = (
	service: RequestServiceCategorySource,
): string | null => {
	const currentRelations = Array.isArray(service?.categories_services_providers)
		? service.categories_services_providers
		: [];
	for (const relation of currentRelations) {
		const categoryName =
			relation?.categories_providers?.categories_services?.name_category;
		if (typeof categoryName === "string" && categoryName.trim()) {
			return categoryName.trim();
		}
	}

	const legacyRelations = Array.isArray(service?.categoriesServices_servicesProviders)
		? service.categoriesServices_servicesProviders
		: [];
	for (const relation of legacyRelations) {
		const categoryName = relation?.categoriesServices?.name_category;
		if (typeof categoryName === "string" && categoryName.trim()) {
			return categoryName.trim();
		}
	}

	return null;
};

type FeedbackModalVariant = "success" | "error" | "info" | "warning";

type FeedbackModalConfig = {
	title: string;
	message?: string;
	variant?: FeedbackModalVariant;
	confirmText?: string;
	cancelText?: string;
	showCancel?: boolean;
	requireInput?: boolean;
	inputLabel?: string;
	inputPlaceholder?: string;
	initialValue?: string;
	validator?: (value: string) => string | null;
};

type FeedbackModalResult = { action: "confirm"; value?: string } | { action: "cancel" };

const MODAL_VARIANT_STYLES: Record<
	FeedbackModalVariant,
	{ Icon: LucideIcon; iconClass: string; badgeClass: string }
> = {
	success: {
		Icon: CheckCircle2,
		iconClass: "text-emerald-600",
		badgeClass: "bg-emerald-50",
	},
	error: {
		Icon: XCircle,
		iconClass: "text-rose-600",
		badgeClass: "bg-rose-50",
	},
	info: {
		Icon: Info,
		iconClass: "text-sky-600",
		badgeClass: "bg-sky-50",
	},
	warning: {
		Icon: AlertTriangle,
		iconClass: "text-amber-600",
		badgeClass: "bg-amber-50",
	},
};

const parseDisplayPrice = (raw?: string) => {
	if (!raw || raw.trim() === "-") return null;
	const normalized = raw
		.replace(/[^\d,.-]/g, "")
		.replace(/\./g, "")
		.replace(",", ".");
	const parsed = Number(normalized);
	return Number.isFinite(parsed) ? parsed : null;
};

const UI_TO_BACKEND_DAY: Record<DayAvailability["day"], ApiSchedule["day_of_week"]> = {
	Lunes: "Lunes",
	Martes: "Martes",
	Miércoles: "Miercoles",
	Jueves: "Jueves",
	Viernes: "Viernes",
	Sábado: "Sabado",
	Domingo: "Domingo",
};

const toTime24 = (value: string, period: "AM" | "PM"): string | null => {
	const match = /^(0?[1-9]|1[0-2]):([0-5]\d)$/.exec(value.trim());
	if (!match) return null;
	const hour = Number(match[1]);
	const minute = Number(match[2]);
	const hour24 = period === "PM" ? (hour % 12) + 12 : hour % 12;
	return `${String(hour24).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

export const ServiceProfile = () => {
	const { session } = useAuthSessionContext();
	const location = useLocation() as {
		state?: {
			initialTab?: "solicitudes" | "pedidos" | "estadisticas" | "cuenta";
		};
	};
	const navigate = useNavigate();
	const [detailsInitialTab, setDetailsInitialTab] = useState<
		"solicitudes" | "pedidos" | "estadisticas" | "cuenta" | undefined
	>(location.state?.initialTab);
	const [usdToBsRate, setUsdToBsRate] = useState(36);
	const [showAddService, setShowAddService] = useState(false);
	const [editingService, setEditingService] = useState<ServiceRow | null>(null);
	const [providerId, setProviderId] = useState<number | null>(null);
	const [pendingCompletionOrder, setPendingCompletionOrder] = useState<OrderRow | null>(
		null,
	);
	const [isCompletingWithImages, setIsCompletingWithImages] = useState(false);
	const [createServiceError, setCreateServiceError] = useState<string | null>(null);
	const [isCreatingService, setIsCreatingService] = useState(false);
	const [personalCategoryIds, setPersonalCategoryIds] = useState<number[]>([]);
	const { data: assignedBadgesData } = useAssignedBadges(providerId ?? 0);
	const { create: createPortfolioWithImages } = useCreatePortfolio();
	const { data: categoriesCatalog } = useCategories();
	const {
		categoryMap,
		serviceCategories,
		subcategoryMapByCategoryId,
		hasLoaded: hasLoadedProviderCategories,
		reloadCategories,
	} = useProviderServiceCategories(providerId);
	const faqCategories = useMemo<FaqCategory[]>(() => {
		const fromCatalog = (Array.isArray(categoriesCatalog) ? categoriesCatalog : [])
			.filter(
				(category) =>
					typeof category?.id === "number" &&
					category.id > 0 &&
					typeof category?.name_category === "string" &&
					category.name_category.trim().length > 0 &&
					category.is_active !== false,
			)
			.map((category) => ({
				id: category.id,
				label: category.name_category.trim(),
			}));

		if (fromCatalog.length > 0) {
			return fromCatalog.sort((a, b) =>
				a.label.localeCompare(b.label, "es", { sensitivity: "base" }),
			);
		}

		return serviceCategories
			.map((category) => ({
				id: category.id,
				label: category.label,
			}))
			.sort((a, b) =>
				a.label.localeCompare(b.label, "es", { sensitivity: "base" }),
			);
	}, [categoriesCatalog, serviceCategories]);
	const effectiveServiceCategories = useMemo(() => {
		if (serviceCategories.length > 0) return serviceCategories;

		if (!Array.isArray(categoriesCatalog) || personalCategoryIds.length === 0) {
			return [] as typeof serviceCategories;
		}

		const allowedCategoryIds = new Set(personalCategoryIds);

		return categoriesCatalog
			.filter(
				(category) =>
					typeof category?.id === "number" &&
					category.id > 0 &&
					allowedCategoryIds.has(category.id) &&
					typeof category?.name_category === "string" &&
					category.name_category.trim().length > 0,
			)
			.map((category) => ({
				id: category.id,
				label: category.name_category.trim(),
				subcategories: Array.isArray(category.subcategories_services)
					? category.subcategories_services
							.map((sub) => sub?.name_subcategory?.trim())
							.filter((name): name is string => Boolean(name))
					: [],
			}));
	}, [categoriesCatalog, personalCategoryIds, serviceCategories]);
	const { create: createOrder } = useCreateOrder();
	const { create: createSchedule } = useCreateSchedule();
	const { toggle: toggleServiceActive } = useToggleServiceActive();
	const { create: createFaqResource } = useCreateFaq();
	const { update: updateFaqResource } = useUpdateFaq();
	const { remove: deleteFaqResource } = useDeleteFaq();
	const { fetchByProvider: fetchFaqsByProvider } = useFaqsByProvider(undefined, false);
	const { fetchByProvider: fetchAccountsByProvider } = useAccountsByProvider(
		undefined,
		false,
	);
	const { data: methodsCatalogData, execute: refetchMethodsCatalog } = useMethods();
	const { create: createAccountResource } = useCreateAccount();
	const { update: updateAccountResource } = useUpdateAccount();
	const { remove: deleteAccountResource } = useDeleteAccount();
	const { execute: createSubcategoryProvider } = useFetch<unknown>({
		endpoint: "/subcategory-provider",
		method: "POST",
		auto: false,
	});
	const { execute: patchScheduleHook } = useFetch<unknown>({
		endpoint: "/schedules",
		method: "PATCH",
		auto: false,
	});
	const createScheduleRef = useRef(createSchedule);
	const patchScheduleHookRef = useRef(patchScheduleHook);

	useEffect(() => {
		createScheduleRef.current = createSchedule;
	}, [createSchedule]);

	useEffect(() => {
		patchScheduleHookRef.current = patchScheduleHook;
	}, [patchScheduleHook]);

	const fetchGet = useCallback(
		async <T,>(endpoint: string) => (await requestApi<T>("GET", endpoint)) as T,
		[],
	);
	const fetchPost = useCallback(
		async <T,>(endpoint: string, body: Record<string, unknown>) =>
			(await requestApi<T>("POST", endpoint, body)) as T,
		[],
	);
	const fetchPatch = useCallback(
		async <T,>(endpoint: string, body: Record<string, unknown>) =>
			(await requestApi<T>("PATCH", endpoint, body)) as T,
		[],
	);
	const fetchDelete = useCallback(
		async <T,>(endpoint: string) => (await requestApi<T>("DELETE", endpoint)) as T,
		[],
	);
	const persistProviderScheduleRows = useCallback(
		async (
			targetProviderId: number,
			schedule: DayAvailability[],
			idsByDay: Partial<Record<DayAvailability["day"], number>>,
		) => {
			for (const slot of schedule) {
				const existingId = idsByDay[slot.day];
				if (slot.enabled) {
					const start24 = toTime24(slot.startTime, slot.startPeriod);
					const end24 = toTime24(slot.endTime, slot.endPeriod);
					if (!start24 || !end24) {
						throw new Error(`Horario inválido en ${slot.day}`);
					}

					if (existingId) {
						await patchScheduleHookRef.current({
							endpoint: `/schedules/${existingId}`,
							body: {
								provider_id: targetProviderId,
								day_of_week: UI_TO_BACKEND_DAY[slot.day],
								start_time: start24,
								end_time: end24,
							},
						});
						await patchScheduleHookRef.current({
							endpoint: `/schedules/${existingId}/deactivate`,
							body: { isActive: true },
						});
					} else {
						await createScheduleRef.current({
							provider_id: targetProviderId,
							day_of_week: UI_TO_BACKEND_DAY[
								slot.day
							] as import("../core/types/services/schedule").DayOfWeek,
							start_time: start24,
							end_time: end24,
						});
					}
				} else if (existingId) {
					await patchScheduleHookRef.current({
						endpoint: `/schedules/${existingId}/deactivate`,
						body: { isActive: false },
					});
				}
			}
		},
		[],
	);
	const [profile, setProfile] = useState<{
		name: string;
		tagline: string;
		since: string;
		location: string;
		rating: number;
		reviews: number;
		avatar: string;
		verified: boolean;
		experience: string;
	} | null>(null);
	const [personalInfo, setPersonalInfo] = useState<ProfessionalPersonalInfo | null>(
		null,
	);
	const [providerAddresses, setProviderAddresses] = useState<ProviderAddressRow[]>([]);
	const [paymentMethods, setPaymentMethods] = useState<PaymentMethodRow[]>([]);
	const [serviceRows, setServiceRows] = useState<ServiceRow[]>([]);
	const [faqRows, setFaqRows] = useState<FaqRow[]>([]);
	const [selectedService, setSelectedService] = useState<ServiceRow | null>(null);
	const [scheduleRows, setScheduleRows] = useState<DayAvailability[]>(
		createDefaultAvailability(),
	);
	const [scheduleIdByDay, setScheduleIdByDay] = useState<
		Partial<Record<DayAvailability["day"], number>>
	>({});
	const [isSavingPersonalInfo, setIsSavingPersonalInfo] = useState(false);
	const [isSavingSchedule, setIsSavingSchedule] = useState(false);
	const [isSavingFaq, setIsSavingFaq] = useState(false);
	const [isSavingAddress, setIsSavingAddress] = useState(false);
	const [isSavingPaymentMethod, setIsSavingPaymentMethod] = useState(false);
	const [togglingServiceId, setTogglingServiceId] = useState<number | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [requestsRefreshKey, setRequestsRefreshKey] = useState(0);
	const [feedbackModalConfig, setFeedbackModalConfig] =
		useState<FeedbackModalConfig | null>(null);
	const [feedbackModalInput, setFeedbackModalInput] = useState("");
	const [feedbackModalError, setFeedbackModalError] = useState<string | null>(null);
	const methodsCatalog = useMemo(
		() => (Array.isArray(methodsCatalogData) ? methodsCatalogData : []),
		[methodsCatalogData],
	);

	useEffect(() => {
		if (!session?.user?.id) return;
		void refetchMethodsCatalog().catch(() => null);
	}, [refetchMethodsCatalog, session?.user?.id]);
	const obtainedBadges = useMemo<ObtainedBadge[]>(() => {
		const assignments = Array.isArray(assignedBadgesData) ? assignedBadgesData : [];
		const unique = new Map<number, ObtainedBadge>();

		(assignments as BadgeAssignmentResponse[]).forEach((assignment) => {
			const badge = assignment.badges;
			if (!badge || typeof badge.id !== "number" || badge.id <= 0) return;
			if (unique.has(badge.id)) return;

			unique.set(badge.id, {
				id: badge.id,
				name: badge.name_badge,
				description: badge.description_badge,
				icon: getImageUri(badge.icon_badge) || badge.icon_badge,
				type: badge.type_badge,
			});
		});

		return [...unique.values()];
	}, [assignedBadgesData]);

	const paymentMethodOptions = useMemo(
		() =>
			methodsCatalog
				.map((method) => {
					const methodType = mapMethodNameToUiType(method.name_method);
					if (!methodType) return null;

					return {
						id: method.id,
						value: methodType,
						label:
							methodType === "transferencia_bancaria"
								? "Transferencia bancaria"
								: "Pago móvil",
						description:
							methodType === "transferencia_bancaria"
								? "Cuenta bancaria nacional"
								: "Teléfono o cédula",
					};
				})
				.filter(
					(
						option,
					): option is {
						id: number;
						value: PaymentMethodSavePayload["methodType"];
						label: string;
						description: string;
					} => Boolean(option),
				),
		[methodsCatalog],
	);

	const personalCategoryLabelById = useMemo(
		() =>
			new Map<number, string>([
				...faqCategories.map(
					(category) => [category.id, category.label] as const,
				),
				...serviceCategories.map(
					(category) => [category.id, category.label] as const,
				),
			]),
		[faqCategories, serviceCategories],
	);

	const serviceNamesByCategoryKey = useMemo(() => {
		const grouped = new Map<string, string[]>();

		serviceRows.forEach((service) => {
			const key = normalizeLookupKey(service.categoria ?? "");
			if (!key || key === "-") return;

			const serviceName =
				(service.nombre ?? service.subcategoria ?? "").trim() ||
				`Servicio ${service.id}`;
			const existing = grouped.get(key) ?? [];
			existing.push(serviceName);
			grouped.set(key, existing);
		});

		for (const [key, names] of grouped.entries()) {
			grouped.set(key, Array.from(new Set(names)));
		}

		return grouped;
	}, [serviceRows]);

	const feedbackModalResolverRef = useRef<
		((result: FeedbackModalResult) => void) | null
	>(null);
	const syncingPendingCategoriesRef = useRef<Set<number>>(new Set());

	const closeFeedbackModal = useCallback((result: FeedbackModalResult) => {
		setFeedbackModalConfig(null);
		feedbackModalResolverRef.current?.(result);
		feedbackModalResolverRef.current = null;
	}, []);

	const showFeedbackModal = useCallback(
		(config: FeedbackModalConfig) =>
			new Promise<FeedbackModalResult>((resolve) => {
				feedbackModalResolverRef.current = resolve;
				setFeedbackModalError(null);
				setFeedbackModalInput(config.initialValue ?? "");
				setFeedbackModalConfig({ variant: "info", ...config });
			}),
		[],
	);

	const handleFeedbackModalConfirm = useCallback(() => {
		if (!feedbackModalConfig) return;
		if (feedbackModalConfig.requireInput) {
			const trimmedValue = feedbackModalInput.trim();
			const validationMessage = feedbackModalConfig.validator?.(trimmedValue);
			if (validationMessage) {
				setFeedbackModalError(validationMessage);
				return;
			}
			if (!trimmedValue) {
				setFeedbackModalError("Este campo es obligatorio.");
				return;
			}
			closeFeedbackModal({ action: "confirm", value: trimmedValue });
			return;
		}
		closeFeedbackModal({ action: "confirm" });
	}, [closeFeedbackModal, feedbackModalConfig, feedbackModalInput]);

	const handleFeedbackModalCancel = useCallback(() => {
		closeFeedbackModal({ action: "cancel" });
	}, [closeFeedbackModal]);

	useEffect(() => {
		if (!providerId) {
			setPersonalCategoryIds([]);
			return;
		}

		setPersonalCategoryIds(
			serviceCategories
				.map((category) => category.id)
				.filter((id): id is number => typeof id === "number" && id > 0),
		);
	}, [providerId, serviceCategories]);

	useEffect(() => {
		if (!providerId || !hasLoadedProviderCategories) return;

		const pendingCategoryIds = getPendingProviderCategories(providerId);
		if (!pendingCategoryIds || pendingCategoryIds.length === 0) return;

		const missingCategoryIds = pendingCategoryIds.filter(
			(categoryId) => !personalCategoryIds.includes(categoryId),
		);

		if (missingCategoryIds.length === 0) {
			clearPendingProviderCategories(providerId);
			return;
		}

		if (syncingPendingCategoriesRef.current.has(providerId)) return;

		let cancelled = false;
		syncingPendingCategoriesRef.current.add(providerId);

		(async () => {
			try {
				await Promise.all(
					missingCategoryIds.map((categoryId) =>
						fetchPost("/categorie-provider", {
							category_id: categoryId,
							provider_id: providerId,
						}),
					),
				);

				if (cancelled) return;
				clearPendingProviderCategories(providerId);
				reloadCategories();
				setPersonalCategoryIds((prev) =>
					Array.from(new Set([...prev, ...missingCategoryIds])),
				);
			} catch (error) {
				const message = error instanceof Error ? error.message.toLowerCase() : "";
				if (message.includes("ya tiene asignada esta categoria")) {
					if (cancelled) return;
					clearPendingProviderCategories(providerId);
					reloadCategories();
					return;
				}
				// Keep pending categories for the next authenticated attempt.
			} finally {
				syncingPendingCategoriesRef.current.delete(providerId);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [
		fetchPost,
		hasLoadedProviderCategories,
		personalCategoryIds,
		providerId,
		reloadCategories,
	]);

	useEffect(() => {
		let cancelled = false;
		const HOURLY_INTERVAL_MS = 60 * 60 * 1000;

		const loadExchangeRate = async () => {
			try {
				const response = await fetchGet<
					| { usd_to_bs?: number | string | null }
					| {
							exchange_rate?: {
								usd_to_bs?: number | string | null;
							} | null;
					  }
				>("/payments/exchange-rate");

				let rawRate: number | string | null | undefined = null;
				if (typeof response === "object" && response !== null) {
					if ("usd_to_bs" in response) {
						rawRate = response.usd_to_bs;
					} else if (
						"exchange_rate" in response &&
						typeof response.exchange_rate === "object" &&
						response.exchange_rate !== null
					) {
						rawRate = response.exchange_rate.usd_to_bs;
					}
				}

				const parsedRate = Number(rawRate);
				if (!cancelled && Number.isFinite(parsedRate) && parsedRate > 0) {
					setUsdToBsRate(parsedRate);
				}
			} catch {
				// keep fallback rate
			}
		};

		void loadExchangeRate();
		const intervalId = window.setInterval(() => {
			void loadExchangeRate();
		}, HOURLY_INTERVAL_MS);

		return () => {
			cancelled = true;
			window.clearInterval(intervalId);
		};
	}, [fetchGet]);

	const parsePriceNumber = (raw?: string) => {
		if (!raw) return 0;
		const normalized = raw
			.replace(/[^\d.,-]/g, "")
			.replace(/\./g, "")
			.replace(",", ".");
		const parsed = Number(normalized);
		return Number.isFinite(parsed) ? parsed : 0;
	};

	const normalizeSubcategorySelection = (subcategoryValues: string[] = []) => {
		const normalized = subcategoryValues
			.map((subcategory) => subcategory?.trim())
			.filter((subcategory): subcategory is string => Boolean(subcategory));

		return normalized.length > 0 ? [normalized[0]] : [];
	};

	const mapServicesToRows = (services: ServicesByProviderResponse["services"] = []) =>
		services.map<ServiceRow>((service) => {
			const relations = service.categories_services_providers ?? [];
			const subcategoryRelationIds = (
				relations[0]?.subcategories_services_providers ?? []
			)
				.map((relation) => {
					const relationId = relation?.id;
					if (typeof relationId !== "number" || relationId <= 0) return null;
					const subcategoryId =
						typeof relation?.subcategory_id === "number"
							? relation.subcategory_id
							: typeof relation?.subcategories_services?.id === "number"
								? relation.subcategories_services.id
								: null;
					return { id: relationId, subcategoryId };
				})
				.filter(
					(
						relation,
					): relation is { id: number; subcategoryId: number | null } =>
						Boolean(relation),
				);

			return {
				id: service.id,
				categoria: resolveServiceCategoryName(service),
				subcategoria: resolveServiceSubcategoryNames(service),
				nombre: service.name_service || undefined,
				subcategorias: extractSelectedSubcategoryNames(service),
				precio: formatPrice(service.base_price),
				activo: Boolean(service.is_active),
				descripcion: service.description_service || undefined,
				duracion: service.estimated_duration ?? null,
				garantia: service.guarantee_days ?? null,
				estimatedTimeHours:
					service.estimatedTimeHours ?? service.estimated_duration ?? null,
				categoryServiceProviderId: relations[0]?.id ?? null,
				subcategoryRelationIds,
				pictures: Array.isArray(service.pictures_service)
					? service.pictures_service
					: [],
				includes: parseMultilineTools(service.includes_tools),
				excludes: parseMultilineTools(service.not_includes_tools),
			};
		});

	const fetchProviderServices = useCallback(
		async (targetProviderId: number) => {
			const endpoints = [
				`/services-provider/provider/${targetProviderId}?page=1&pageSize=500`,
				`/services-provider/provider/${targetProviderId}`,
			];

			for (const endpoint of endpoints) {
				const response = await fetchGet<unknown>(endpoint).catch(() => null);
				if (!response) continue;

				const services = extractServices(response);
				if (services.length > 0) {
					return services;
				}

				if (endpoint === endpoints[endpoints.length - 1]) {
					return services;
				}
			}

			return [];
		},
		[fetchGet],
	);

	const refreshProviderServices = async (targetProviderId: number) => {
		const services = await fetchProviderServices(targetProviderId);
		setServiceRows(mapServicesToRows(services));
	};

	const refreshProviderFaqs = async (targetProviderId: number) => {
		const refreshed = await fetchFaqsByProvider(targetProviderId).catch(() => []);
		const faqs = Array.isArray(refreshed) ? refreshed : [];
		setFaqRows(mapFaqsToRows(faqs));
	};

	const refreshProviderAddresses = async (targetUserId: number) => {
		const userPayload = await fetchGet<unknown>(`/users/${targetUserId}`).catch(
			() => null,
		);
		const addressesFromUser = extractAddressesFromUserPayload(userPayload);
		if (addressesFromUser.length > 0) {
			setProviderAddresses(mapProviderAddressesToRows(addressesFromUser));
			return;
		}

		const refreshed = await fetchGet<unknown>(
			`/addresses/provider/${targetUserId}`,
		).catch(() => []);
		const addresses = extractAddresses(refreshed);
		setProviderAddresses(mapProviderAddressesToRows(addresses));
	};

	const resolvePaymentHolderName = (fallbackName?: string) => {
		const candidate =
			fallbackName?.trim() || personalInfo?.nombre?.trim() || profile?.name?.trim();
		return candidate && candidate.length > 0 ? candidate : "Titular de la cuenta";
	};

	const refreshProviderPaymentMethods = async (
		targetProviderId: number,
		fallbackHolderName?: string,
	) => {
		const refreshed = await fetchAccountsByProvider(targetProviderId).catch(() => []);
		setPaymentMethods(
			mapProviderAccountsToPaymentRows(
				Array.isArray(refreshed) ? refreshed : [],
				methodsCatalog,
				resolvePaymentHolderName(fallbackHolderName),
			),
		);
		return Array.isArray(refreshed) ? refreshed : [];
	};

	const normalizePrimaryProviderAccount = async (
		accounts: AccountResponse[],
		preferredPrimaryId?: number,
	) => {
		const activeAccounts = accounts.filter(
			(account) => account.is_active === undefined || account.is_active,
		);

		if (activeAccounts.length === 0) return false;

		const hasPreferredPrimary =
			typeof preferredPrimaryId === "number" &&
			activeAccounts.some((account) => account.id === preferredPrimaryId);

		const currentPrimaryId = activeAccounts.find((account) => account.is_primary)?.id;

		const desiredPrimaryId = hasPreferredPrimary
			? preferredPrimaryId
			: (currentPrimaryId ?? activeAccounts[0].id);

		const pendingUpdates = activeAccounts
			.filter((account) => account.is_primary !== (account.id === desiredPrimaryId))
			.map((account) =>
				updateAccountResource(account.id, {
					is_primary: account.id === desiredPrimaryId,
				} satisfies UpdateAccountPayload),
			);

		if (pendingUpdates.length === 0) {
			return false;
		}

		await Promise.all(pendingUpdates);
		return true;
	};

	useEffect(() => {
		let cancelled = false;

		const BACKEND_TO_UI_DAY: Record<
			ApiSchedule["day_of_week"],
			DayAvailability["day"]
		> = {
			Lunes: "Lunes",
			Martes: "Martes",
			Miercoles: "Miércoles",
			Jueves: "Jueves",
			Viernes: "Viernes",
			Sabado: "Sábado",
			Domingo: "Domingo",
		};

		const parseTime24 = (
			value?: string | null,
		): { time: string; period: "AM" | "PM" } | null => {
			if (!value) return null;
			const match = /^(\d{1,2}):(\d{2})/.exec(value.trim());
			if (!match) return null;
			const hour24 = Number(match[1]);
			const minute = Number(match[2]);
			if (
				!Number.isFinite(hour24) ||
				!Number.isFinite(minute) ||
				hour24 < 0 ||
				hour24 > 23 ||
				minute < 0 ||
				minute > 59
			)
				return null;
			const period: "AM" | "PM" = hour24 >= 12 ? "PM" : "AM";
			const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
			return {
				time: `${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
				period,
			};
		};

		const formatDate = (value?: string | null) => {
			if (!value) return "-";
			const date = new Date(value);
			return Number.isNaN(date.getTime())
				? value
				: new Intl.DateTimeFormat("es-VE", {
						day: "2-digit",
						month: "long",
						year: "numeric",
					}).format(date);
		};

		const formatLocation = (addresses?: ApiAddress[]) => {
			const address = Array.isArray(addresses)
				? (addresses.find((item) => item.is_active !== false) ?? addresses[0])
				: undefined;
			if (!address) return "Sin ubicación";
			const stateName =
				address.name_state ||
				address.states?.name_state ||
				address.states?.name_complete ||
				null;
			const cityName =
				address.name_city ||
				address.municipalities?.name_municipality ||
				address.municipalities?.name_complete ||
				null;
			const municipalityName =
				address.municipalities?.name_municipality ||
				address.municipalities?.name_complete ||
				null;
			const parishName = address.parishes?.name_parish || null;
			const parts = [cityName, parishName, municipalityName, stateName].filter(
				Boolean,
			);
			return parts.length ? parts.join(", ") : "Sin ubicación";
		};

		const loadServices = async (id: number) => {
			const services = await fetchProviderServices(id);
			return mapServicesToRows(services);
		};

		const loadSchedules = async (providerId: number) => {
			const payload = await fetchGet<unknown>(
				`/schedules/provider/${providerId}`,
			).catch(() => ({ schedules: [] }));
			const schedules = extractSchedules(payload);
			const byDay = new Map<DayAvailability["day"], DayAvailability>();
			const ids: Partial<Record<DayAvailability["day"], number>> = {};

			schedules.forEach((schedule) => {
				const uiDay = BACKEND_TO_UI_DAY[schedule.day_of_week];
				if (!uiDay) return;
				const start = parseTime24(schedule.start_time);
				const end = parseTime24(schedule.end_time);
				if (!start || !end) return;

				byDay.set(uiDay, {
					day: uiDay,
					enabled: Boolean(schedule.is_active),
					startTime: start.time,
					startPeriod: start.period,
					endTime: end.time,
					endPeriod: end.period,
				});
				ids[uiDay] = schedule.id;
			});

			const defaults = createDefaultAvailability();
			return {
				schedules: defaults.map(
					(defaultDay) => byDay.get(defaultDay.day) ?? defaultDay,
				),
				ids,
			};
		};

		const loadFaqs = async (providerId: number) => {
			const payload = await fetchFaqsByProvider(providerId).catch(() => []);
			const faqs = Array.isArray(payload) ? payload : [];
			return mapFaqsToRows(faqs);
		};

		const loadProfile = async () => {
			if (!session?.user?.id || session.user.type_user !== "Proveedor") {
				setPaymentMethods([]);
				setIsLoading(false);
				return;
			}

			setIsLoading(true);
			setError(null);
			try {
				type ProviderUserProfile = {
					id?: number;
					names?: string | null;
					last_names?: string | null;
					email?: string | null;
					phone?: string | null;
					created_at?: string | null;
					addresses_user?: string | null;
					image_profile?: string | null;
					addresses?: ApiAddress[];
					date_of_birth?: string | null;
					gender_user?: string | null;
					providers?: {
						id?: number | null;
						verified_provider?: boolean | null;
					} | null;
				};
				type UserProfilePayload =
					| ProviderUserProfile
					| { user?: ProviderUserProfile };

				const userPayload = await fetchGet<UserProfilePayload>(
					`/users/${session.user.id}`,
				).catch(() => null);

				const userFromUsersEndpoint: ProviderUserProfile | null = (() => {
					if (!userPayload) return null;
					const wrapped = userPayload as { user?: ProviderUserProfile };
					if (typeof wrapped.user !== "undefined") return wrapped.user ?? null;
					return userPayload as ProviderUserProfile;
				})();

				const providerIdFromUser = userFromUsersEndpoint?.providers?.id ?? null;
				if (!providerIdFromUser) throw new Error("Proveedor no encontrado");

				const provider = await fetchGet<ApiProvider>(
					`/providers/${providerIdFromUser}`,
				);

				const user = userFromUsersEndpoint ?? provider.users ?? null;
				const fullName = [user?.names, user?.last_names]
					.filter(Boolean)
					.join(" ");
				const displayName = fullName || `Proveedor #${provider.id}`;
				const since = formatDate(
					provider.member_from ||
						provider.created_at ||
						user?.created_at ||
						null,
				);

				const [
					addressesPayload,
					mappedServices,
					schedulesPayloadBase,
					mappedFaqs,
					providerAccounts,
				] = await Promise.all([
					fetchGet<unknown>(`/addresses/provider/${session.user.id}`).catch(
						() => [],
					),
					loadServices(provider.id).catch(() => []),
					loadSchedules(provider.id).catch(() => ({
						schedules: createDefaultAvailability(),
						ids: {},
					})),
					loadFaqs(provider.id).catch(() => []),
					fetchAccountsByProvider(provider.id).catch(() => []),
				]);

				let schedulesPayload = schedulesPayloadBase;
				const pendingSchedule = getPendingProviderSchedule(provider.id);
				if (pendingSchedule && pendingSchedule.length > 0) {
					try {
						await persistProviderScheduleRows(
							provider.id,
							pendingSchedule,
							schedulesPayload.ids,
						);
						clearPendingProviderSchedule(provider.id);
						schedulesPayload = await loadSchedules(provider.id).catch(
							() => schedulesPayloadBase,
						);
					} catch {
						// Keep loading normally if pending schedule sync fails.
					}
				}

				const fullAddressFromUser =
					user?.addresses_user?.trim() ||
					provider.users?.addresses_user?.trim();
				const addressesFromUser = Array.isArray(user?.addresses)
					? user.addresses
					: [];
				const loadedProviderAddresses = mapProviderAddressesToRows(
					addressesFromUser.length > 0
						? addressesFromUser
						: extractAddresses(addressesPayload),
				);
				const fallbackMappedServices = mapServicesToRows(
					Array.isArray(provider.services_providers)
						? provider.services_providers
						: [],
				);
				const resolvedMappedServices =
					mappedServices.length > 0 ? mappedServices : fallbackMappedServices;
				const location = fullAddressFromUser
					? fullAddressFromUser
					: formatLocation(extractAddresses(addressesPayload));
				const experienceYearsValue =
					typeof provider.experience_years === "number" &&
					Number.isFinite(provider.experience_years) &&
					provider.experience_years > 0
						? provider.experience_years
						: null;
				const experienceYearsInput =
					experienceYearsValue !== null ? String(experienceYearsValue) : "";
				setProviderId(provider.id);

				if (!cancelled) {
					setProfile({
						name: displayName,
						tagline:
							provider.description_provider || "Descripción no disponible",
						since,
						location,
						rating: provider.rating ?? 0,
						reviews: resolveProviderReviewsCount(provider),
						avatar: resolveProfileAvatar(
							user?.image_profile,
							provider.users?.image_profile,
						),
						verified: Boolean(provider.verified_provider),
						experience:
							experienceYearsValue !== null
								? `${experienceYearsValue} ${experienceYearsValue === 1 ? "año" : "años"} de experiencia`
								: NO_EXPERIENCE_MESSAGE,
					});
					setPersonalInfo({
						nombre: displayName,
						oficio: provider.company_name || "Proveedor",
						companyName: provider.company_name || "",
						experiencia: experienceYearsInput,
						idiomas: provider.languages_provider || "Sin idiomas registrados",
						ubicacion: location,
						desde: since,
						verificado: Boolean(provider.verified_provider),
						correo: user?.email || "-",
						telefono: user?.phone || "-",
						biografia:
							provider.description_provider || "Sin biografía profesional",
						categoryIds: [],
						fechaNacimiento: userFromUsersEndpoint?.date_of_birth ?? null,
						genero: userFromUsersEndpoint?.gender_user ?? null,
					});
					setServiceRows(resolvedMappedServices);
					setFaqRows(mappedFaqs);
					setProviderAddresses(loadedProviderAddresses);
					setPaymentMethods(
						mapProviderAccountsToPaymentRows(
							Array.isArray(providerAccounts) ? providerAccounts : [],
							methodsCatalog,
							displayName,
						),
					);
					setScheduleRows(schedulesPayload.schedules);
					setScheduleIdByDay(schedulesPayload.ids);
				}
			} catch (err) {
				if (!cancelled) {
					setError(
						err instanceof Error
							? err.message
							: "No se pudo cargar el perfil",
					);
					setProfile(null);
					setPersonalInfo(null);
					setServiceRows([]);
					setFaqRows([]);
					setProviderAddresses([]);
					setPaymentMethods([]);
					setProviderId(null);
					setScheduleRows(createDefaultAvailability());
					setScheduleIdByDay({});
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};

		loadProfile();
		return () => {
			cancelled = true;
		};
	}, [
		session?.user?.id,
		session?.user?.type_user,
		fetchProviderServices,
		fetchFaqsByProvider,
		fetchAccountsByProvider,
		fetchGet,
		methodsCatalog,
		persistProviderScheduleRows,
	]);

	// ── Pedidos del proveedor (hook + derived) ──
	const { data: rawOrdersData, refreshProviderOrders } = useProviderOrders(
		providerId,
		1,
		50,
	);

	const [providerRequests, setProviderRequests] = useState<
		Array<{
			id: string;
			orderId?: number | null;
			requestId?: number | null;
			servicio: string;
			cliente: string;
			clientId?: number | null;
			fecha: string;
			estado: ProviderStatusLabel;
			total: string;
			description?: string;
			urgency?: string;
			categoria?: string;
			subcategoria?: string;
			ubicacion?: string;
			requestItems?: Array<{
				name: string;
				quantity: number;
				unitPrice: number;
				lineTotal: number;
			}>;
			requestDate?: string;
			requestTime?: string;
		}>
	>([]);

	const serviceCategoryByName = useMemo(() => {
		const categoryByName = new Map<string, string>();

		for (const service of serviceRows) {
			const category = service.categoria?.trim();
			if (!category) continue;

			const candidates = [
				service.nombre,
				service.subcategoria,
				...(Array.isArray(service.subcategorias) ? service.subcategorias : []),
			]
				.map((value) => String(value ?? "").trim())
				.filter((value) => value.length > 0);

			for (const candidate of candidates) {
				const normalized = normalizeLookupKey(candidate);
				if (!normalized) continue;
				if (!categoryByName.has(normalized)) {
					categoryByName.set(normalized, category);
				}
			}
		}

		return categoryByName;
	}, [serviceRows]);

	const serviceCategoryById = useMemo(() => {
		const categoryById = new Map<number, string>();

		for (const service of serviceRows) {
			const category = service.categoria?.trim();
			if (!category) continue;
			if (typeof service.id !== "number" || service.id <= 0) continue;
			if (!categoryById.has(service.id)) {
				categoryById.set(service.id, category);
			}
		}

		return categoryById;
	}, [serviceRows]);

	const providerOrders = useMemo(() => {
		const apiOrders = extractApiOrders(rawOrdersData);
		if (apiOrders.length === 0) return [];

		const requestsById = new Map<number, (typeof providerRequests)[number]>();
		const requestsByClient = new Map<
			number,
			Array<(typeof providerRequests)[number]>
		>();

		for (const request of providerRequests) {
			if (typeof request.requestId === "number" && request.requestId > 0) {
				requestsById.set(request.requestId, request);
			}
			if (typeof request.clientId === "number" && request.clientId > 0) {
				const existing = requestsByClient.get(request.clientId) ?? [];
				existing.push(request);
				requestsByClient.set(request.clientId, existing);
			}
		}

		const seenIds = new Set<number>();
		const mapped: Array<{
			id: string;
			orderId?: number | null;
			numeroPedido?: string;
			orderDateRaw?: string;
			requestId?: number | null;
			servicio: string;
			cliente: string;
			clientId?: number | null;
			fecha: string;
			estado: ProviderStatusLabel;
			total: string;
			totalAmount?: number | null;
			description?: string;
			urgency?: string;
			categoria?: string;
			subcategoria?: string;
			ubicacion?: string;
			hasPendingQuotation?: boolean;
		}> = [];

		for (const order of apiOrders) {
			if (seenIds.has(order.id)) continue;
			seenIds.add(order.id);
			const backendOrderCode = order.code_order?.trim();
			const quotation = Array.isArray(order.quotations)
				? order.quotations[0]
				: order.quotations;

			type ProviderQuotation = NonNullable<OrderResponse["quotations"]>[number];
			const allQuotations: ProviderQuotation[] = Array.isArray(order.quotations)
				? order.quotations
				: order.quotations
					? [order.quotations as ProviderQuotation]
					: [];
			const hasPendingQuotation = allQuotations.some((quotation) => {
				const quotationId = Number(quotation.id ?? 0);
				if (!Number.isFinite(quotationId) || quotationId <= 0) return false;
				const statusRaw = quotation.status_quotation;
				const isActive =
					typeof statusRaw === "boolean" ? statusRaw : statusRaw !== false;
				if (!isActive) return false;
				return !quotation.date_accepted && !quotation.date_rejected;
			});

			const request = order.requests ?? quotation?.requests;
			const parsedDescription = parseOrderRequestDescription(
				request?.description_request,
			);
			const service = request?.services_providers;
			const serviceName = service?.name_service?.trim() || "";
			const serviceId =
				typeof service?.id === "number" && service.id > 0 ? service.id : null;
			const categoryFromServiceRows = serviceName
				? serviceCategoryByName.get(normalizeLookupKey(serviceName))
				: undefined;
			const categoryFromServiceRowsById =
				serviceId !== null ? serviceCategoryById.get(serviceId) : undefined;
			const fallbackRequest =
				(typeof request?.id === "number" && request.id > 0
					? requestsById.get(request.id)
					: undefined) ??
				(typeof order.request_id === "number" && order.request_id > 0
					? requestsById.get(order.request_id)
					: undefined) ??
				(typeof order.client_id === "number" && order.client_id > 0
					? ((requestsByClient.get(order.client_id) ?? []).find(
							(candidate) =>
								candidate.estado === "Dialogando" ||
								candidate.estado === "En Espera" ||
								candidate.estado === "En Proceso",
						) ?? (requestsByClient.get(order.client_id) ?? [])[0])
					: undefined);
			const category =
				(parsedDescription?.category?.trim() ||
					resolveRequestServiceCategory(service) ||
					fallbackRequest?.categoria ||
					categoryFromServiceRowsById ||
					categoryFromServiceRows) ??
				null;
			const clientFullName = [
				order.clients?.users?.names,
				order.clients?.users?.last_names,
			]
				.filter((part): part is string => Boolean(part && part.trim()))
				.join(" ");
			const subtotal = quotation?.proposed_price;
			const parsedTotal = Number(parsedDescription?.total);
			const derivedItemsTotal = (parsedDescription?.items ?? []).reduce(
				(acc, item) => acc + Number(item?.lineTotal ?? 0),
				0,
			);
			const orderPrice = Number(order.order_price ?? NaN);
			const orderPriceAux = Number(order.order_price_aux ?? NaN);
			const effectiveTotal =
				Number.isFinite(orderPrice) && orderPrice > 0
					? orderPrice
					: Number.isFinite(orderPriceAux) && orderPriceAux > 0
						? orderPriceAux
						: typeof subtotal === "number"
							? subtotal
							: Number.isFinite(parsedTotal)
								? parsedTotal
								: derivedItemsTotal > 0
									? derivedItemsTotal
									: parseDisplayPrice(fallbackRequest?.total);
			const rawDate =
				order.date_initial_order ??
				order.created_at ??
				order.estimated_date_completion;
			const address = [
				order.addresses?.street_address,
				order.addresses?.reference_address,
			]
				.filter((value): value is string => Boolean(value && value.trim()))
				.join(", ");

			mapped.push({
				id: `order-${order.id}`,
				orderId: order.id,
				numeroPedido: backendOrderCode || String(order.id),
				orderDateRaw: rawDate ?? undefined,
				requestId:
					request?.id ?? order.request_id ?? fallbackRequest?.requestId ?? null,
				servicio:
					service?.name_service ??
					fallbackRequest?.subcategoria ??
					fallbackRequest?.servicio ??
					request?.title_request ??
					request?.description_request ??
					"Servicio",
				cliente: clientFullName || "Cliente",
				clientId: order.client_id ?? null,
				fecha: rawDate ? new Date(rawDate).toLocaleDateString() : "-",
				estado: mapBackendStatusToUi(order.status_order),
				total:
					typeof effectiveTotal === "number"
						? formatPrice(effectiveTotal)
						: "-",
				totalAmount: typeof effectiveTotal === "number" ? effectiveTotal : null,
				description:
					request?.description_request ??
					fallbackRequest?.description ??
					order.status_reason ??
					undefined,
				urgency:
					parsedDescription?.urgency ?? fallbackRequest?.urgency ?? undefined,
				categoria: category ?? undefined,
				subcategoria:
					service?.name_service ?? fallbackRequest?.subcategoria ?? undefined,
				ubicacion: address || undefined,
				hasPendingQuotation,
			});
		}

		return mapped;
	}, [rawOrdersData, providerRequests, serviceCategoryById, serviceCategoryByName]);

	const providerStats = useMemo<ProfessionalStats>(() => {
		const completedStatuses = new Set<ProviderStatusLabel>([
			"Completado",
			"Pagado",
			"Garantía",
			"Soporte",
		]);
		const pendingPaymentStatuses = new Set<ProviderStatusLabel>([
			"En Espera",
			"Dialogando",
			"En Proceso",
		]);

		const now = new Date();
		const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
		const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

		let completados = 0;
		let ingreso = 0;
		let porCobrar = 0;
		let pendientesCobro = 0;

		for (const order of providerOrders) {
			const amount =
				typeof order.totalAmount === "number" &&
				Number.isFinite(order.totalAmount)
					? order.totalAmount
					: 0;

			if (completedStatuses.has(order.estado as ProviderStatusLabel)) {
				completados += 1;
				const rawDate = order.orderDateRaw ? new Date(order.orderDateRaw) : null;
				if (
					rawDate &&
					!Number.isNaN(rawDate.getTime()) &&
					rawDate >= startOfMonth &&
					rawDate < startOfNextMonth
				) {
					ingreso += amount;
				}
			}

			if (
				pendingPaymentStatuses.has(order.estado as ProviderStatusLabel) &&
				amount > 0
			) {
				pendientesCobro += 1;
				porCobrar += amount;
			}
		}

		return {
			totalPedidos: providerOrders.length,
			completados,
			ingreso,
			porCobrar,
			pendientesCobro,
			rating:
				typeof profile?.rating === "number" && Number.isFinite(profile.rating)
					? profile.rating
					: 0,
		};
	}, [profile?.rating, providerOrders]);

	useEffect(() => {
		if (!providerId || serviceRows.length === 0) {
			setProviderRequests([]);
			return;
		}

		let cancelled = false;

		type ApiRequest = {
			id: number;
			title_request?: string | null;
			description_request?: string | null;
			proposed_price_request?: number | null;
			minimun_budget?: number | null;
			maximun_budget?: number | null;
			urgency_request?: string | null;
			created_at?: string | null;
			status_request?: string | null;
			clients?: {
				id?: number | null;
				users?: { names?: string | null; last_names?: string | null } | null;
			} | null;
			addresses?: {
				street_address?: string | null;
				reference_address?: string | null;
			} | null;
			services_providers?: {
				id?: number | null;
				name_service?: string | null;
				categories_services_providers?: Array<{
					categories_providers?: {
						categories_services?: { name_category?: string | null } | null;
					} | null;
				}> | null;
				categoriesServices_servicesProviders?: Array<{
					categoriesServices?: {
						name_category?: string | null;
					} | null;
				}> | null;
			} | null;
		};

		type RequestsResponse = { items?: ApiRequest[]; requests?: ApiRequest[] };

		const mapRequestStatusToUi = (status?: string | null): ProviderStatusLabel => {
			const normalized = String(status ?? "")
				.toLowerCase()
				.trim();
			if (normalized.includes("rechaz") || normalized.includes("cancel"))
				return "Cancelado";
			if (normalized.includes("pagad")) return "Pagado";
			if (normalized.includes("complet")) return "Completado";
			if (normalized.includes("garantia") || normalized.includes("garantía"))
				return "Garantía";
			if (normalized.includes("soporte")) return "Soporte";
			if (normalized.includes("esper")) return "En Espera";
			if (normalized.includes("acept")) return "En Proceso";
			return "Dialogando";
		};

		const formatRequestBudget = (
			minBudget?: number | null,
			maxBudget?: number | null,
			proposedPrice?: number | null,
		) => {
			const hasProposed =
				typeof proposedPrice === "number" &&
				Number.isFinite(proposedPrice) &&
				proposedPrice > 0;
			const hasMin = typeof minBudget === "number" && Number.isFinite(minBudget);
			const hasMax = typeof maxBudget === "number" && Number.isFinite(maxBudget);

			if (hasProposed) return formatPrice(Number(proposedPrice));

			if (hasMin && hasMax) {
				if (Number(minBudget) === Number(maxBudget)) {
					return formatPrice(Number(minBudget));
				}
				return `${formatPrice(Number(minBudget))} - ${formatPrice(Number(maxBudget))}`;
			}

			if (hasMin) return formatPrice(Number(minBudget));
			if (hasMax) return formatPrice(Number(maxBudget));
			return "-";
		};

		type StructuredRequestDescription = {
			format?: string;
			urgency?: string;
			category?: string;
			subcategory?: string[];
			schedule?: { date?: string; time?: string };
			items?: Array<{
				name?: string;
				quantity?: number;
				unitPrice?: number;
				lineTotal?: number;
			}>;
			total?: number;
		};

		const parseStructuredDescription = (
			raw?: string | null,
		): StructuredRequestDescription | null => {
			if (!raw) return null;
			const marker = "SERVIX_REQUEST_V1::";
			if (!raw.startsWith(marker)) return null;
			const encoded = raw.slice(marker.length).trim();
			if (!encoded) return null;
			try {
				const parsed = JSON.parse(encoded) as StructuredRequestDescription;
				return parsed?.format === "SERVIX_REQUEST_V1" ? parsed : null;
			} catch {
				return null;
			}
		};

		const loadRequests = async () => {
			const serviceIds = Array.from(new Set(serviceRows.map((row) => row.id)));

			const loadRequestsByServiceId = async (serviceId: number) => {
				const candidates = [
					`/requests/services/${serviceId}?page=1&pageSize=50`,
					`/requests/service/${serviceId}?page=1&pageSize=50`,
					`/requests/services-provider/${serviceId}?page=1&pageSize=50`,
				];

				for (const endpoint of candidates) {
					try {
						const response = await fetchGet<RequestsResponse>(endpoint);
						return response;
					} catch {
						// try next endpoint variant
					}
				}

				return { items: [] } satisfies RequestsResponse;
			};

			const responses = await Promise.all(
				serviceIds.map((serviceId) => loadRequestsByServiceId(serviceId)),
			);

			const seenRequestIds = new Set<number>();
			const mapped: typeof providerRequests = [];

			for (const response of responses) {
				const requestItems = Array.isArray(response?.items)
					? response.items
					: Array.isArray(response?.requests)
						? response.requests
						: [];

				for (const request of requestItems) {
					if (seenRequestIds.has(request.id)) continue;
					seenRequestIds.add(request.id);
					const parsedDescription = parseStructuredDescription(
						request.description_request,
					);

					const serviceName =
						request.services_providers?.name_service?.trim() || "";
					const categoryFromRelation = resolveRequestServiceCategory(
						request.services_providers,
					);
					const categoryFromServiceRows = serviceName
						? serviceCategoryByName.get(normalizeLookupKey(serviceName))
						: undefined;
					const category =
						parsedDescription?.category?.trim() ||
						categoryFromRelation ||
						categoryFromServiceRows ||
						null;
					const subcategory =
						(parsedDescription?.subcategory?.[0]?.trim() ||
							request.services_providers?.name_service) ??
						request.title_request ??
						"Solicitud de servicio";
					const fullClientName = [
						request.clients?.users?.names,
						request.clients?.users?.last_names,
					]
						.filter((part): part is string => Boolean(part && part.trim()))
						.join(" ");
					const requestItemsDetail = (parsedDescription?.items ?? [])
						.map((item) => {
							const quantity = Number(item.quantity ?? 0);
							const unitPrice = Number(item.unitPrice ?? 0);
							const lineTotal = Number(
								item.lineTotal ?? quantity * unitPrice,
							);
							const name = item.name?.trim() || "Servicio";
							if (!Number.isFinite(quantity) || quantity <= 0) return null;
							return {
								name,
								quantity,
								unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
								lineTotal: Number.isFinite(lineTotal) ? lineTotal : 0,
							};
						})
						.filter(
							(
								item,
							): item is {
								name: string;
								quantity: number;
								unitPrice: number;
								lineTotal: number;
							} => Boolean(item),
						);

					const parsedTotal = Number(parsedDescription?.total);
					const derivedItemsTotal = requestItemsDetail.reduce(
						(acc, item) => acc + item.lineTotal,
						0,
					);
					const effectiveTotal = Number.isFinite(parsedTotal)
						? parsedTotal
						: derivedItemsTotal > 0
							? derivedItemsTotal
							: null;
					const budgetText =
						typeof effectiveTotal === "number" && effectiveTotal > 0
							? formatPrice(effectiveTotal)
							: formatRequestBudget(
									request.minimun_budget,
									request.maximun_budget,
									request.proposed_price_request,
								);
					const address = [
						request.addresses?.street_address,
						request.addresses?.reference_address,
					]
						.filter((value): value is string =>
							Boolean(value && value.trim()),
						)
						.join(", ");

					mapped.push({
						id: `REQ-${request.id}`,
						requestId: request.id,
						servicio: subcategory,
						cliente: fullClientName || "Cliente",
						clientId: request.clients?.id ?? null,
						fecha: request.created_at
							? new Date(request.created_at).toLocaleDateString()
							: "-",
						estado: mapRequestStatusToUi(request.status_request),
						total: budgetText,
						description: request.description_request ?? undefined,
						urgency:
							parsedDescription?.urgency ??
							request.urgency_request ??
							undefined,
						categoria: category ?? undefined,
						subcategoria: subcategory,
						ubicacion: address || undefined,
						requestItems:
							requestItemsDetail.length > 0
								? requestItemsDetail
								: undefined,
						requestDate: parsedDescription?.schedule?.date,
						requestTime: parsedDescription?.schedule?.time,
					});
				}
			}

			if (!cancelled) setProviderRequests(mapped);
		};

		void loadRequests();

		return () => {
			cancelled = true;
		};
	}, [providerId, serviceRows, requestsRefreshKey, fetchGet, serviceCategoryByName]);

	if (!session?.user?.id) {
		return <Navigate to='/login' replace />;
	}

	if (session.user.type_user === "Cliente") {
		return <Navigate to='/user-profile' replace />;
	}

	const refreshSchedules = async (targetProviderId: number) => {
		const payload = await fetchGet<unknown>(
			`/schedules/provider/${targetProviderId}`,
		).catch(() => ({ schedules: [] }));
		const schedules = extractSchedules(payload);
		const backendToUi: Record<ApiSchedule["day_of_week"], DayAvailability["day"]> = {
			Lunes: "Lunes",
			Martes: "Martes",
			Miercoles: "Miércoles",
			Jueves: "Jueves",
			Viernes: "Viernes",
			Sabado: "Sábado",
			Domingo: "Domingo",
		};

		const byDay = new Map<DayAvailability["day"], DayAvailability>();
		const ids: Partial<Record<DayAvailability["day"], number>> = {};

		schedules.forEach((schedule) => {
			const day = backendToUi[schedule.day_of_week];
			const startMatch = /^(\d{1,2}):(\d{2})/.exec(schedule.start_time.trim());
			const endMatch = /^(\d{1,2}):(\d{2})/.exec(schedule.end_time.trim());
			if (!day || !startMatch || !endMatch) return;
			const startHour = Number(startMatch[1]);
			const startMinute = Number(startMatch[2]);
			const endHour = Number(endMatch[1]);
			const endMinute = Number(endMatch[2]);
			const startPeriod: "AM" | "PM" = startHour >= 12 ? "PM" : "AM";
			const endPeriod: "AM" | "PM" = endHour >= 12 ? "PM" : "AM";
			const startTime = `${startHour % 12 === 0 ? 12 : startHour % 12}:${String(startMinute).padStart(2, "0")}`;
			const endTime = `${endHour % 12 === 0 ? 12 : endHour % 12}:${String(endMinute).padStart(2, "0")}`;

			byDay.set(day, {
				day,
				enabled: Boolean(schedule.is_active),
				startTime,
				startPeriod,
				endTime,
				endPeriod,
			});
			ids[day] = schedule.id;
		});

		const defaults = createDefaultAvailability();
		setScheduleRows(
			defaults.map((defaultDay) => byDay.get(defaultDay.day) ?? defaultDay),
		);
		setScheduleIdByDay(ids);
	};

	const handleToggleServiceStatus = async (service: ServiceRow) => {
		if (!providerId) return;
		if (togglingServiceId === service.id) return;

		const nextActive = !service.activo;
		setTogglingServiceId(service.id);
		setServiceRows((prev) =>
			prev.map((item) =>
				item.id === service.id ? { ...item, activo: nextActive } : item,
			),
		);

		try {
			await toggleServiceActive(service.id, nextActive);
			await refreshProviderServices(providerId);
		} catch (err) {
			setServiceRows((prev) =>
				prev.map((item) =>
					item.id === service.id ? { ...item, activo: service.activo } : item,
				),
			);
			await showFeedbackModal({
				variant: "error",
				title: "Error",
				message:
					err instanceof Error
						? err.message
						: "No se pudo actualizar el estado del servicio.",
			});
		} finally {
			setTogglingServiceId(null);
		}
	};

	const handleCreateProviderFaq = async (payload: {
		categoryId: number;
		question: string;
		answer: string;
	}) => {
		if (!providerId) return;
		setIsSavingFaq(true);
		try {
			const createPayload: CreateFaqPayload = {
				provider_id: providerId,
				category_id: payload.categoryId,
				question: payload.question.trim(),
				answer: payload.answer.trim(),
				order_display: faqRows.length,
			};
			await createFaqResource(createPayload);
			await refreshProviderFaqs(providerId);
		} finally {
			setIsSavingFaq(false);
		}
	};

	const handleUpdateProviderFaq = async (
		faqId: number,
		payload: {
			categoryId: number;
			question: string;
			answer: string;
		},
	) => {
		if (!providerId) return;
		setIsSavingFaq(true);
		try {
			const updatePayload: UpdateFaqPayload = {
				category_id: payload.categoryId,
				question: payload.question.trim(),
				answer: payload.answer.trim(),
			};
			await updateFaqResource(faqId, updatePayload);
			await refreshProviderFaqs(providerId);
		} finally {
			setIsSavingFaq(false);
		}
	};

	const handleDeleteProviderFaq = async (faqId: number) => {
		if (!providerId) return;
		setIsSavingFaq(true);
		try {
			await deleteFaqResource(faqId);
			await refreshProviderFaqs(providerId);
		} finally {
			setIsSavingFaq(false);
		}
	};

	const buildProviderAddressPayload = (payload: { state: string; city: string }) => {
		const state = payload.state.trim();
		const city = payload.city.trim();
		return {
			user_id: session.user.id,
			name_country: "Venezuela",
			name_state: state,
			name_city: city,
			name_address: `${city}, ${state}`,
			street_address: city,
			reference_address: state,
		};
	};

	const syncLocationFromAddress = (payload: { state: string; city: string }) => {
		const composedLocation = [payload.city.trim(), payload.state.trim()]
			.filter(Boolean)
			.join(", ");

		if (!composedLocation) return;

		setPersonalInfo((prev) =>
			prev
				? {
						...prev,
						ubicacion: composedLocation,
					}
				: prev,
		);

		setProfile((prev) =>
			prev
				? {
						...prev,
						location: composedLocation,
					}
				: prev,
		);
	};

	const handleCreateProviderAddress = async (payload: {
		state: string;
		city: string;
	}) => {
		if (!session?.user?.id) return;
		setIsSavingAddress(true);
		try {
			const result = await fetchPost<Record<string, unknown>>(
				"/addresses",
				buildProviderAddressPayload(payload),
			);
			const created = (result?.address ?? result) as { id?: number } | undefined;
			if (typeof created?.id === "number") {
				await fetchPatch(`/addresses/${created.id}/reactivate`, {}).catch(
					() => null,
				);
			}
			await refreshProviderAddresses(session.user.id);
			syncLocationFromAddress(payload);
		} finally {
			setIsSavingAddress(false);
		}
	};

	const handleUpdateProviderAddress = async (
		addressId: number,
		payload: { state: string; city: string },
	) => {
		if (!session?.user?.id) return;
		setIsSavingAddress(true);
		try {
			await fetchPatch(`/addresses/${addressId}`, {
				name_country: "Venezuela",
				name_state: payload.state.trim(),
				name_city: payload.city.trim(),
				name_address: `${payload.city.trim()}, ${payload.state.trim()}`,
				street_address: payload.city.trim(),
				reference_address: payload.state.trim(),
			});
			await refreshProviderAddresses(session.user.id);
			syncLocationFromAddress(payload);
		} finally {
			setIsSavingAddress(false);
		}
	};

	const handleToggleProviderAddress = async (
		addressId: number,
		nextActive: boolean,
	) => {
		if (!session?.user?.id) return;
		setIsSavingAddress(true);
		try {
			await fetchPatch(
				nextActive
					? `/addresses/${addressId}/reactivate`
					: `/addresses/${addressId}/status`,
				{},
			);
			await refreshProviderAddresses(session.user.id);
		} finally {
			setIsSavingAddress(false);
		}
	};

	const toAccountPayloadFromPaymentMethod = (
		payload: PaymentMethodSavePayload,
	): Omit<CreateAccountPayload, "provider_id"> => {
		const normalizedBankName = payload.bankName.trim();
		const normalizedAccountNumber = (payload.accountNumber ?? "").replace(/\D/g, "");
		const normalizedPhoneNumber = (payload.phoneNumber ?? "").replace(/\D/g, "");
		const normalizedIdentification = (payload.identification ?? "").replace(
			/\D/g,
			"",
		);

		const resolvedMethodId =
			typeof payload.methodId === "number" && payload.methodId > 0
				? payload.methodId
				: resolveMethodIdByUiType(payload.methodType, methodsCatalog);

		if (!resolvedMethodId) {
			throw new Error("No se pudo resolver el método de pago seleccionado.");
		}

		return {
			name_bank: normalizedBankName,
			number_account:
				payload.methodType === "transferencia_bancaria"
					? normalizedAccountNumber
					: normalizedPhoneNumber || normalizedAccountNumber,
			method_id: resolvedMethodId,
			number_phone:
				payload.methodType === "pago_movil" ? normalizedPhoneNumber : undefined,
			identification:
				payload.methodType === "pago_movil"
					? normalizedIdentification
					: undefined,
			is_primary: payload.isDefault,
		};
	};

	const syncProviderPaymentMethods = async (
		targetProviderId: number,
		options?: {
			preferredPrimaryId?: number;
			holderName?: string;
		},
	) => {
		const accounts = await refreshProviderPaymentMethods(
			targetProviderId,
			options?.holderName,
		);

		const normalized = await normalizePrimaryProviderAccount(
			accounts,
			options?.preferredPrimaryId,
		);

		if (normalized) {
			await refreshProviderPaymentMethods(targetProviderId, options?.holderName);
		}
	};

	const handleCreatePaymentMethod = async (payload: PaymentMethodSavePayload) => {
		if (!providerId) return;
		setIsSavingPaymentMethod(true);
		try {
			const createPayload: CreateAccountPayload = {
				provider_id: providerId,
				...toAccountPayloadFromPaymentMethod(payload),
			};

			const created = await createAccountResource(createPayload);
			await syncProviderPaymentMethods(providerId, {
				preferredPrimaryId:
					payload.isDefault && typeof created?.id === "number"
						? created.id
						: undefined,
				holderName: payload.holderName,
			});
		} finally {
			setIsSavingPaymentMethod(false);
		}
	};

	const handleUpdatePaymentMethod = async (
		paymentMethodId: number,
		payload: PaymentMethodSavePayload,
	) => {
		if (!providerId) return;
		setIsSavingPaymentMethod(true);
		try {
			await updateAccountResource(paymentMethodId, {
				...toAccountPayloadFromPaymentMethod(payload),
			} satisfies UpdateAccountPayload);

			await syncProviderPaymentMethods(providerId, {
				preferredPrimaryId: payload.isDefault ? paymentMethodId : undefined,
				holderName: payload.holderName,
			});
		} finally {
			setIsSavingPaymentMethod(false);
		}
	};

	const handleDeletePaymentMethod = async (paymentMethodId: number) => {
		if (!providerId) return;
		setIsSavingPaymentMethod(true);
		try {
			await deleteAccountResource(paymentMethodId);
			await syncProviderPaymentMethods(providerId);
		} finally {
			setIsSavingPaymentMethod(false);
		}
	};

	const handleSavePersonalInfo = async (payload: ProfessionalPersonalInfo) => {
		if (!providerId) return false;
		const payloadWithSplitName = payload as ProfessionalPersonalInfo & {
			firstName?: string;
			lastName?: string;
		};

		const fullName = (payload.nombre || "").trim();
		const normalizedCompanyName = (
			payload.companyName ??
			payload.oficio ??
			""
		).trim();
		const nameParts = fullName.split(/\s+/).filter(Boolean);
		const normalizedFirstName =
			payloadWithSplitName.firstName?.trim() || nameParts[0] || "";
		const normalizedLastName =
			payloadWithSplitName.lastName?.trim() ||
			(nameParts.length > 1 ? nameParts.slice(1).join(" ") : "");
		const normalizedFullName =
			`${normalizedFirstName} ${normalizedLastName}`.trim() || fullName;
		const normalizedExperienceInput = String(payload.experiencia || "")
			.replace(/[^\d]/g, "")
			.trim();
		const experienceYearsMatch = normalizedExperienceInput.match(/\d+/);
		const experienceYears = experienceYearsMatch
			? Number(experienceYearsMatch[0])
			: undefined;
		const normalizedExperienceYears =
			typeof experienceYears === "number" &&
			Number.isFinite(experienceYears) &&
			experienceYears > 0
				? experienceYears
				: undefined;
		const normalizedCategoryIds = Array.isArray(payload.categoryIds)
			? Array.from(
					new Set(
						payload.categoryIds.filter(
							(categoryId): categoryId is number =>
								typeof categoryId === "number" &&
								Number.isFinite(categoryId) &&
								categoryId > 0,
						),
					),
				)
			: [];
		const missingCategoryIds = normalizedCategoryIds.filter(
			(categoryId) => !personalCategoryIds.includes(categoryId),
		);
		const removedCategoryIds = personalCategoryIds.filter(
			(categoryId) => !normalizedCategoryIds.includes(categoryId),
		);
		const blockedRemovedCategories = removedCategoryIds
			.map((categoryId) => {
				const label =
					personalCategoryLabelById.get(categoryId)?.trim() ||
					`Categoría ${categoryId}`;
				return {
					id: categoryId,
					label,
					key: normalizeLookupKey(label),
				};
			})
			.filter((category) => serviceNamesByCategoryKey.has(category.key));
		const normalizedPhone = (payload.telefono ?? "").trim();
		const phoneError = getPhoneValidationError(normalizedPhone);
		if (phoneError) throw new Error(phoneError);

		if (blockedRemovedCategories.length > 0) {
			const restoredCategoryIds = Array.from(
				new Set([
					...normalizedCategoryIds,
					...blockedRemovedCategories.map((category) => category.id),
				]),
			);
			setPersonalCategoryIds(restoredCategoryIds);
			setPersonalInfo((prev) =>
				prev
					? {
							...prev,
							categoryIds: restoredCategoryIds,
						}
					: prev,
			);

			const blockedSummaries = blockedRemovedCategories.map((category) => {
				const uniqueServiceNames =
					serviceNamesByCategoryKey.get(category.key) ?? [];
				return {
					label: category.label,
					services: uniqueServiceNames,
				};
			});

			const detailsText = blockedSummaries
				.map((summary) => {
					if (summary.services.length === 0) {
						return `${summary.label}: tiene servicios asociados.`;
					}
					return `${summary.label}: ${summary.services.join(", ")}.`;
				})
				.join("\n");

			await showFeedbackModal({
				variant: "warning",
				title: "No se puede eliminar la categoría",
				message:
					blockedSummaries.length === 1
						? `No puedes eliminar la categoría "${blockedSummaries[0].label}" porque tiene servicios asociados.\nServicios: ${blockedSummaries[0].services.join(", ") || "No disponibles"}.`
						: `No puedes eliminar estas categorías porque tienen servicios asociados:\n${detailsText}`,
			});
			return false;
		}

		setIsSavingPersonalInfo(true);
		try {
			await Promise.all([
				fetchPatch(`/users/${session.user.id}`, {
					...(normalizedFirstName ? { names: normalizedFirstName } : {}),
					...(normalizedLastName ? { last_names: normalizedLastName } : {}),
					...(payload.correo?.trim() ? { email: payload.correo.trim() } : {}),
					...(normalizedPhone ? { phone: normalizedPhone } : {}),
					...(payload.fechaNacimiento !== undefined ? { date_of_birth: payload.fechaNacimiento } : {}),
					...(payload.genero !== undefined ? { gender_user: payload.genero } : {}),
				}),
				fetchPatch(`/providers/${providerId}`, {
					...(normalizedCompanyName
						? { company_name: normalizedCompanyName }
						: {}),
					...(payload.biografia?.trim()
						? { description_provider: payload.biografia.trim() }
						: {}),
					...(payload.idiomas?.trim()
						? { languages_provider: payload.idiomas.trim() }
						: {}),
					...(typeof normalizedExperienceYears === "number"
						? { experience_years: normalizedExperienceYears }
						: {}),
				}),
				...missingCategoryIds.map((categoryId) =>
					fetchPost("/categorie-provider", {
						category_id: categoryId,
						provider_id: providerId,
					}),
				),
			]);

			if (removedCategoryIds.length > 0) {
				const providerCategoriesPayload = await fetchGet<unknown>(
					`/categorie-provider/provider/${providerId}`,
				).catch(() => null);
				const providerRelations = extractCategoryProviderRelations(
					providerCategoriesPayload,
				);
				const removedIdSet = new Set(removedCategoryIds);
				const relationIdsToDelete = providerRelations
					.filter((relation) =>
						removedIdSet.has(Number(relation.categories_services?.id ?? 0)),
					)
					.map((relation) => relation.id)
					.filter(
						(relationId) => typeof relationId === "number" && relationId > 0,
					);

				if (relationIdsToDelete.length > 0) {
					await Promise.all(
						relationIdsToDelete.map((relationId) =>
							fetchDelete(`/categorie-provider/${relationId}`),
						),
					);
				}
			}

			reloadCategories();

			setPersonalCategoryIds(normalizedCategoryIds);

			setPersonalInfo((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					nombre: normalizedFullName || prev.nombre,
					oficio: normalizedCompanyName || prev.oficio,
					companyName: normalizedCompanyName || prev.companyName || prev.oficio,
					experiencia:
						typeof normalizedExperienceYears === "number"
							? normalizedExperienceInput
							: "",
					idiomas: payload.idiomas?.trim() || prev.idiomas,
					correo: payload.correo?.trim() || prev.correo,
					telefono: normalizedPhone || prev.telefono,
					biografia: payload.biografia?.trim() || prev.biografia,
					categoryIds: normalizedCategoryIds,
					fechaNacimiento: payload.fechaNacimiento !== undefined ? payload.fechaNacimiento : prev.fechaNacimiento,
					genero: payload.genero !== undefined ? payload.genero : prev.genero,
				};
			});

			setProfile((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					name: normalizedFullName || prev.name,
					tagline: payload.biografia?.trim() || prev.tagline,
					experience:
						typeof normalizedExperienceYears === "number"
							? `${normalizedExperienceYears} ${normalizedExperienceYears === 1 ? "año" : "años"} de experiencia`
							: NO_EXPERIENCE_MESSAGE,
				};
			});

			const nextHolderName = normalizedFullName;
			if (nextHolderName) {
				setPaymentMethods((prev) =>
					prev.map((method) => ({
						...method,
						holderName: nextHolderName,
					})),
				);
			}

			await refreshSchedules(providerId);
			await showFeedbackModal({
				variant: "success",
				title: "Guardado",
				message: "Perfil actualizado correctamente.",
			});

			return true;
		} catch (err) {
			await showFeedbackModal({
				variant: "error",
				title: "Error",
				message:
					err instanceof Error
						? err.message
						: "No se pudo actualizar el perfil",
			});
			return false;
		} finally {
			setIsSavingPersonalInfo(false);
		}
	};

	const handleSaveSchedule = async (schedule: DayAvailability[]) => {
		if (!providerId) return;
		setIsSavingSchedule(true);
		try {
			await persistProviderScheduleRows(providerId, schedule, scheduleIdByDay);

			await refreshSchedules(providerId);
			await showFeedbackModal({
				variant: "success",
				title: "Guardado",
				message: "Horario actualizado correctamente.",
			});
		} catch (err) {
			await showFeedbackModal({
				variant: "error",
				title: "Error",
				message:
					err instanceof Error
						? err.message
						: "No se pudo actualizar el horario",
			});
			throw err;
		} finally {
			setIsSavingSchedule(false);
		}
	};

	const handleCreateService = async (payload: {
		categoryId?: number;
		category: string;
		subcategory: string[];
		name: string;
		price: string;
		warrantyDays: string;
		estimatedTimeHours: string;
		description: string;
		includes: string[];
		excludes: string[];
		faqs?: Array<{ question: string; answer: string }>;
		photos?: File[];
	}) => {
		setIsCreatingService(true);
		setCreateServiceError(null);
		try {
			if (typeof providerId !== "number") {
				throw new Error("No se encontró el ID del proveedor.");
			}
			const normalizedCategory = payload.category
				? normalizeLookupKey(payload.category)
				: "";
			const categoryId =
				payload.categoryId ??
				(normalizedCategory ? categoryMap[normalizedCategory] : undefined);

			if (!categoryId) {
				throw new Error("Selecciona una categoría válida del listado");
			}

			if (!Array.isArray(payload.photos) || payload.photos.length === 0) {
				throw new Error(
					"Debes cargar al menos una imagen para crear el servicio.",
				);
			}
			const form = new FormData();
			form.append("provider_id", String(providerId));
			form.append("category_service_id", String(categoryId));
			form.append("name_service", payload.name);
			form.append("description_service", payload.description);
			form.append("base_price", String(payload.price));
			form.append(
				"estimated_duration",
				String(Number(payload.estimatedTimeHours ?? 1)),
			);
			form.append("guarantee_days", String(payload.warrantyDays));
			const includesText = payload.includes.join("\n");
			const excludesText = payload.excludes.join("\n");
			form.append("includes_tools", includesText);
			form.append("not_includes_tools", excludesText);
			for (const photo of payload.photos ?? []) {
				form.append("pictures_service", photo);
			}

			const created = await postFormData<{
				id?: number;
				service?: { id?: number };
			}>(`/services-provider`, form);
			const createdServiceId = created?.service?.id ?? created?.id ?? null;

			const selectedSubcategoryNames = normalizeSubcategorySelection(
				payload.subcategory ?? [],
			);

			const selectedSubcategoryIds = selectedSubcategoryNames
				.map(
					(sub) =>
						subcategoryMapByCategoryId[categoryId]?.[normalizeLookupKey(sub)],
				)
				.filter((id): id is number => typeof id === "number" && id > 0);

			if (createdServiceId && selectedSubcategoryIds.length > 0) {
				const createdServicePayload = await fetchGet<{
					service?: {
						categories_services_providers?: Array<{ id?: number | null }>;
					};
					categories_services_providers?: Array<{ id?: number | null }>;
				}>(`/services-provider/${createdServiceId}`).catch(() => null);

				const relationId =
					createdServicePayload?.service?.categories_services_providers?.[0]
						?.id ??
					createdServicePayload?.categories_services_providers?.[0]?.id ??
					null;

				if (typeof relationId === "number" && relationId > 0) {
					await Promise.all(
						Array.from(new Set(selectedSubcategoryIds)).map((subcategoryId) =>
							createSubcategoryProvider({
								body: {
									category_service_provider_id: relationId,
									subcategory_id: subcategoryId,
								},
							}),
						),
					);
				}
			}

			const faqs = (payload.faqs ?? [])
				.map((item) => ({
					question: item.question?.trim() ?? "",
					answer: item.answer?.trim() ?? "",
				}))
				.filter((item) => item.question.length > 0 && item.answer.length > 0)
				.slice(0, 4);

			if (faqs.length > 0) {
				await Promise.all(
					faqs.map((item, index) =>
						createFaqResource({
							provider_id: providerId,
							category_id: categoryId,
							question: item.question,
							answer: item.answer,
							order_display: index,
							service_provider_id: createdServiceId ?? undefined,
						}),
					),
				);
			}

			await refreshProviderServices(providerId);
			setShowAddService(false);
			setEditingService(null);
		} catch (err) {
			setCreateServiceError(
				err instanceof Error ? err.message : "No se pudo crear el servicio",
			);
			throw err;
		} finally {
			setIsCreatingService(false);
		}
	};

	const handleUpdateService = async (
		service: ServiceRow,
		payload: {
			categoryId?: number;
			category: string;
			subcategory: string[];
			name: string;
			price: string;
			warrantyDays: string;
			estimatedTimeHours: string;
			description: string;
			includes: string[];
			excludes: string[];
			photos?: File[];
		},
	) => {
		if (!providerId) return;
		setIsCreatingService(true);
		setCreateServiceError(null);
		try {
			const normalizedCategory = payload.category
				? normalizeLookupKey(payload.category)
				: "";
			const categoryId =
				payload.categoryId ??
				(normalizedCategory ? categoryMap[normalizedCategory] : undefined);

			if (!categoryId) {
				throw new Error("Selecciona una categoría válida del listado");
			}

			const hasNewPhotos =
				Array.isArray(payload.photos) && payload.photos.length > 0;
			if (hasNewPhotos) {
				const form = new FormData();
				form.append("provider_id", String(providerId));
				form.append("category_service_id", String(categoryId));
				form.append("name_service", payload.name);
				form.append("description_service", payload.description);
				form.append("base_price", String(parsePriceNumber(payload.price)));
				form.append(
					"estimated_duration",
					String(
						Number(
							payload.estimatedTimeHours ?? service.estimatedTimeHours ?? 1,
						),
					),
				);
				form.append(
					"guarantee_days",
					String(Number(payload.warrantyDays || service.garantia || 0)),
				);
				form.append("includes_tools", payload.includes.join("\n"));
				form.append("not_includes_tools", payload.excludes.join("\n"));

				for (const photo of payload.photos ?? []) {
					form.append("pictures_service", photo);
				}

				await patchFormData(`/services-provider/${service.id}`, form);
			} else {
				await fetchPatch(`/services-provider/${service.id}`, {
					provider_id: providerId,
					category_service_id: categoryId,
					name_service: payload.name,
					description_service: payload.description,
					base_price: parsePriceNumber(payload.price),
					estimated_duration: Number(
						payload.estimatedTimeHours ?? service.estimatedTimeHours ?? 1,
					),
					guarantee_days: Number(payload.warrantyDays || service.garantia || 0),
					includes_tools: payload.includes.join("\n"),
					not_includes_tools: payload.excludes.join("\n"),
				});
			}

			const selectedSubcategoryIds = Array.from(
				new Set(
					normalizeSubcategorySelection(payload.subcategory ?? [])
						.map(
							(sub) =>
								subcategoryMapByCategoryId[categoryId]?.[
									normalizeLookupKey(sub)
								],
						)
						.filter((id): id is number => typeof id === "number" && id > 0),
				),
			);

			let relationId = service.categoryServiceProviderId ?? null;
			let existingRelations = service.subcategoryRelationIds ?? [];

			const servicePayload = await fetchGet<{
				service?: {
					categories_services_providers?: Array<{
						id?: number | null;
						categories_providers?: {
							category_id?: number | null;
							categories_services?: { id?: number | null } | null;
						} | null;
						subcategories_services_providers?: Array<{
							id?: number | null;
							subcategory_id?: number | null;
							subcategories_services?: { id?: number | null } | null;
						}>;
					}>;
				};
				categories_services_providers?: Array<{
					id?: number | null;
					categories_providers?: {
						category_id?: number | null;
						categories_services?: { id?: number | null } | null;
					} | null;
					subcategories_services_providers?: Array<{
						id?: number | null;
						subcategory_id?: number | null;
						subcategories_services?: { id?: number | null } | null;
					}>;
				}>;
			}>(`/services-provider/${service.id}`).catch(() => null);

			const fetchedRelations = toArray(
				servicePayload?.service?.categories_services_providers ??
					servicePayload?.categories_services_providers ??
					null,
			);

			const matchedRelation =
				fetchedRelations.find((relation) => {
					const relationCategoryId =
						typeof relation?.categories_providers?.category_id === "number"
							? relation.categories_providers.category_id
							: typeof relation?.categories_providers?.categories_services
										?.id === "number"
								? relation.categories_providers.categories_services.id
								: null;
					return relationCategoryId === categoryId;
				}) ??
				fetchedRelations[0] ??
				null;

			if (matchedRelation) {
				relationId = matchedRelation.id ?? relationId;
				existingRelations =
					toArray(matchedRelation.subcategories_services_providers)
						.map((item) => {
							const id = item?.id;
							if (typeof id !== "number" || id <= 0) return null;
							const subcategoryId =
								typeof item?.subcategory_id === "number"
									? item.subcategory_id
									: typeof item?.subcategories_services?.id === "number"
										? item.subcategories_services.id
										: null;
							return { id, subcategoryId };
						})
						.filter(
							(
								item,
							): item is { id: number; subcategoryId: number | null } =>
								Boolean(item),
						) ?? existingRelations;
			}

			if (relationId && relationId > 0) {
				const currentSubcategoryIds = new Set(
					existingRelations
						.map((item) => item.subcategoryId)
						.filter((id): id is number => typeof id === "number" && id > 0),
				);
				const targetSubcategoryIds = new Set(selectedSubcategoryIds);

				const toCreate = selectedSubcategoryIds.filter(
					(id) => !currentSubcategoryIds.has(id),
				);
				const toDelete = existingRelations.filter(
					(item) =>
						typeof item.subcategoryId === "number" &&
						item.subcategoryId > 0 &&
						!targetSubcategoryIds.has(item.subcategoryId),
				);

				if (toCreate.length > 0) {
					await Promise.all(
						toCreate.map((subcategoryId) =>
							createSubcategoryProvider({
								body: {
									category_service_provider_id: relationId,
									subcategory_id: subcategoryId,
								},
							}),
						),
					);
				}

				if (toDelete.length > 0) {
					await Promise.all(
						toDelete.map((relation) =>
							fetchDelete(`/subcategory-provider/${relation.id}`),
						),
					);
				}
			}

			await refreshProviderServices(providerId);
			setShowAddService(false);
			setEditingService(null);
		} catch (err) {
			setCreateServiceError(
				err instanceof Error ? err.message : "No se pudo actualizar el servicio",
			);
			throw err;
		} finally {
			setIsCreatingService(false);
		}
	};

	const extractRequestId = (requestRow: { id: string; requestId?: number | null }) => {
		if (typeof requestRow.requestId === "number" && requestRow.requestId > 0) {
			return requestRow.requestId;
		}
		const fallback = Number(String(requestRow.id).replace(/\D/g, ""));
		return Number.isFinite(fallback) && fallback > 0 ? fallback : null;
	};

	const handleAcceptRequest = async (requestRow: {
		id: string;
		requestId?: number | null;
		total?: string;
		clientId?: number | null;
		cliente?: string;
		servicio?: string;
	}) => {
		if (!providerId) return;
		const requestId = extractRequestId(requestRow);
		if (!requestId) {
			await showFeedbackModal({
				variant: "error",
				title: "Error",
				message: "No se pudo identificar la solicitud a aceptar.",
			});
			return;
		}

		let requestAccepted = false;
		try {
			await fetchPatch(`/requests/${requestId}/status`, { status: "Aceptada" });
			requestAccepted = true;

			const detail = await fetchGet<{
				id: number;
				title_request?: string | null;
				description_request?: string | null;
				proposed_price_request?: number | null;
				minimun_budget?: number | null;
				date_request?: string | null;
				deadline_request?: string | null;
				clients?: {
					id?: number | null;
					users?: {
						id?: number | null;
						names?: string | null;
						last_names?: string | null;
					} | null;
				} | null;
				addresses?: { id?: number | null } | null;
			}>(`/requests/${requestId}`);

			const clientId = detail?.clients?.id;
			const addressId = detail?.addresses?.id;
			const estimatedDate = detail?.deadline_request ?? new Date().toISOString();

			if (
				typeof clientId !== "number" ||
				clientId <= 0 ||
				typeof addressId !== "number" ||
				addressId <= 0
			) {
				throw new Error(
					"La solicitud fue aceptada, pero no se pudo crear el pedido por falta de datos del cliente o dirección.",
				);
			}

			let orderPrice =
				typeof detail?.minimun_budget === "number" && detail.minimun_budget > 0
					? detail.minimun_budget
					: 0;

			if (orderPrice <= 0) {
				const marker = "SERVIX_REQUEST_V1::";
				if (
					typeof detail.description_request === "string" &&
					detail.description_request.startsWith(marker)
				) {
					const encoded = detail.description_request
						.slice(marker.length)
						.trim();
					if (encoded) {
						try {
							const parsed = JSON.parse(encoded) as {
								items?: Array<{ lineTotal?: number }>;
							};
							const totalFromItems = (parsed.items ?? []).reduce(
								(acc, item) => {
									const value = Number(item.lineTotal ?? 0);
									return Number.isFinite(value) && value > 0
										? acc + value
										: acc;
								},
								0,
							);
							if (totalFromItems > 0) orderPrice = totalFromItems;
						} catch {
							// ignore malformed structured payload
						}
					}
				}

				if (orderPrice <= 0) {
					const rowTotal = parsePriceNumber(requestRow.total);
					if (rowTotal > 0) orderPrice = rowTotal;
				}
			}

			const createdOrder = await createOrder({
				client_id: clientId,
				provider_id: providerId,
				address_id: addressId,
				request_id: requestId,
				estimated_date_completion: estimatedDate,
				order_price_aux: orderPrice,
			});

			const createdOrderRecord = createdOrder as {
				id?: number | string | null;
				order?: {
					id?: number | string | null;
					clients?: {
						users?: { id?: number | string | null } | null;
					} | null;
					providers?: {
						users?: { id?: number | string | null } | null;
					} | null;
				} | null;
				clients?: {
					users?: { id?: number | string | null } | null;
				} | null;
				providers?: {
					users?: { id?: number | string | null } | null;
				} | null;
				attributes?: {
					id?: number | string | null;
					clients?: {
						users?: { id?: number | string | null } | null;
					} | null;
					providers?: {
						users?: { id?: number | string | null } | null;
					} | null;
				} | null;
			} | null;

			const createdOrderData =
				createdOrderRecord?.order ??
				createdOrderRecord?.attributes ??
				createdOrderRecord;

			const createdOrderId = Number(
				createdOrderData?.id ?? createdOrderRecord?.id ?? 0,
			);
			let initialMessageIssue: string | null = null;

			if (Number.isFinite(createdOrderId) && createdOrderId > 0) {
				const providerUserIdFromOrder = Number(
					createdOrderData?.providers?.users?.id ?? 0,
				);
				const providerUserId =
					providerUserIdFromOrder > 0
						? providerUserIdFromOrder
						: Number(session?.user?.id ?? 0);
				let clientUserId = Number(
					detail?.clients?.users?.id ??
						createdOrderData?.clients?.users?.id ??
						0,
				);

				if (!(Number.isFinite(clientUserId) && clientUserId > 0)) {
					try {
						const orderDetail = await fetchGet<{
							clients?: {
								users?: {
									id?: number | null;
								} | null;
							} | null;
							order?: {
								clients?: {
									users?: {
										id?: number | null;
									} | null;
								} | null;
							} | null;
						}>(`/orders/${createdOrderId}`);

						clientUserId = Number(
							orderDetail?.clients?.users?.id ??
								orderDetail?.order?.clients?.users?.id ??
								0,
						);
					} catch {
						// Continue with clients fallback.
					}
				}

				if (!(Number.isFinite(clientUserId) && clientUserId > 0)) {
					try {
						const clientDetail = await fetchGet<{
							users?: {
								id?: number | null;
								names?: string | null;
								last_names?: string | null;
							} | null;
							client?: {
								users?: {
									id?: number | null;
									names?: string | null;
									last_names?: string | null;
								} | null;
							} | null;
						}>(`/clients/${clientId}`);
						clientUserId = Number(
							clientDetail?.users?.id ??
								clientDetail?.client?.users?.id ??
								0,
						);
					} catch {
						initialMessageIssue =
							"No se pudo obtener el usuario del cliente para iniciar el chat.";
					}
				}

				if (!initialMessageIssue) {
					if (
						!(Number.isFinite(providerUserId) && providerUserId > 0) ||
						!(Number.isFinite(clientUserId) && clientUserId > 0)
					) {
						initialMessageIssue =
							"No se pudo determinar el remitente o destinatario del mensaje inicial.";
					} else {
						const fullClientName = [
							detail?.clients?.users?.names,
							detail?.clients?.users?.last_names,
						]
							.filter((part): part is string =>
								Boolean(part && part.trim()),
							)
							.join(" ");
						const resolvedClientName =
							fullClientName || requestRow.cliente || "cliente";
						const firstName =
							resolvedClientName.trim().split(/\s+/)[0] || "cliente";
						const requestLabel = String(
							detail?.title_request ?? requestRow.servicio ?? "",
						).trim();
						const initialMessage = requestLabel
							? `Hola ${firstName}, he aceptado tu solicitud "${requestLabel}". En breve te comparto la cotización y los siguientes pasos por este chat.`
							: `Hola ${firstName}, he aceptado tu solicitud. En breve te comparto la cotización y los siguientes pasos por este chat.`;

						try {
							await fetchPost("/message", {
								reminent_id: providerUserId,
								recipient_id: clientUserId,
								order_id: createdOrderId,
								content_message: initialMessage,
							});

							if (typeof window !== "undefined") {
								window.dispatchEvent(
									new CustomEvent("servix:chat-refresh", {
										detail: {
											orderId: createdOrderId,
											senderUserId: providerUserId,
											recipientUserId: clientUserId,
										},
									}),
								);
							}
						} catch (messageError) {
							const details =
								messageError instanceof Error && messageError.message
									? ` (${messageError.message})`
									: "";
							initialMessageIssue = `El pedido fue creado, pero no se pudo enviar el mensaje inicial automáticamente${details}.`;
						}
					}
				}

				setRequestsRefreshKey((prev) => prev + 1);
				void refreshProviderOrders();
				await showFeedbackModal({
					variant: initialMessageIssue ? "warning" : "success",
					title: initialMessageIssue
						? "Solicitud aceptada con aviso"
						: "Solicitud aceptada",
					message: initialMessageIssue
						? initialMessageIssue
						: "La solicitud fue aceptada, el pedido fue creado y el mensaje inicial fue enviado.",
				});
				return;
			}

			setRequestsRefreshKey((prev) => prev + 1);
				void refreshProviderOrders();
			await showFeedbackModal({
				variant: "warning",
				title: "Solicitud aceptada con aviso",
				message:
					"La solicitud fue aceptada y el pedido fue creado, pero no se pudo identificar el pedido para enviar el mensaje inicial.",
			});
		} catch (err) {
			if (requestAccepted) {
				setRequestsRefreshKey((prev) => prev + 1);
				await showFeedbackModal({
					variant: "warning",
					title: "Aceptada con pendiente",
					message:
						err instanceof Error
							? err.message
							: "La solicitud se aceptó, pero no se pudo crear el pedido.",
				});
				return;
			}

			await showFeedbackModal({
				variant: "error",
				title: "Error",
				message:
					err instanceof Error
						? err.message
						: "No se pudo aceptar la solicitud",
			});
		}
	};

	const handleRejectRequest = async (requestRow: {
		id: string;
		requestId?: number | null;
	}) => {
		const rejectionModal = await showFeedbackModal({
			variant: "warning",
			title: "Rechazar solicitud",
			message: "Indica el motivo del rechazo antes de continuar.",
			requireInput: true,
			inputLabel: "Motivo del rechazo",
			inputPlaceholder: "Ej. No hay disponibilidad en la fecha solicitada.",
			showCancel: true,
			confirmText: "Rechazar",
			cancelText: "Cancelar",
			validator: (value) => {
				if (!value.trim()) {
					return "Debes indicar un motivo para rechazar la solicitud.";
				}
				if (value.trim().length < 5) {
					return "El motivo debe tener al menos 5 caracteres.";
				}
				return null;
			},
		});

		if (rejectionModal.action !== "confirm" || !rejectionModal.value) {
			return;
		}

		const requestId = extractRequestId(requestRow);
		if (!requestId) {
			await showFeedbackModal({
				variant: "error",
				title: "Error",
				message: "No se pudo identificar la solicitud a rechazar.",
			});
			return;
		}

		try {
			await fetchPatch(`/requests/${requestId}/status`, {
				status: "Rechazada",
				reason: rejectionModal.value.trim(),
			});
			setRequestsRefreshKey((prev) => prev + 1);
			await showFeedbackModal({
				variant: "success",
				title: "Solicitud rechazada",
				message: "La solicitud fue rechazada correctamente.",
			});
		} catch (err) {
			await showFeedbackModal({
				variant: "error",
				title: "Error",
				message:
					err instanceof Error
						? err.message
						: "No se pudo rechazar la solicitud",
			});
		}
	};

	const isCompletedOrderForQuotation = (status?: string) => {
		const normalized = String(status ?? "").toLowerCase();
		return normalized.includes("complet") || normalized.includes("pagad");
	};

	const handleStartQuotation = (order: OrderRow) => {
		if (isCompletedOrderForQuotation(order.estado)) {
			void showFeedbackModal({
				variant: "info",
				title: "Pedido completado",
				message:
					"Este pedido ya está completado y no permite nuevas cotizaciones.",
				confirmText: "Entendido",
			});
			return;
		}

		const orderIdFromRow =
			typeof order.orderId === "number" && order.orderId > 0
				? order.orderId
				: (() => {
						const match = /(\d+)$/.exec(String(order.id ?? ""));
						if (!match) return 0;
						const parsed = Number(match[1]);
						return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
					})();

		if (!orderIdFromRow) {
			void showFeedbackModal({
				variant: "error",
				title: "Pedido inválido",
				message: "No se pudo identificar el pedido para crear la cotización.",
				confirmText: "Entendido",
			});
			return;
		}

		navigate(
			`/elaboracion-cotizacion?requestId=${order.requestId ?? 0}&providerId=${providerId}&orderId=${orderIdFromRow}`,
			{
				state: {
					order: {
						...order,
						orderId: orderIdFromRow,
					},
					providerId,
					providerName: personalInfo?.nombre,
				},
			},
		);
	};

	const resolveProviderOrderId = (order: OrderRow) => {
		if (typeof order.orderId === "number" && order.orderId > 0) {
			return order.orderId;
		}

		const fromCode = Number(order.numeroPedido);
		if (Number.isFinite(fromCode) && fromCode > 0) {
			return fromCode;
		}

		const fromRowId = Number(String(order.id).replace(/\D/g, ""));
		if (Number.isFinite(fromRowId) && fromRowId > 0) {
			return fromRowId;
		}

		return null;
	};

	const updateProviderOrderStatus = async ({
		order,
		status,
		reason,
		successTitle,
		successMessage,
	}: {
		order: OrderRow;
		status: "En_Progreso" | "Completado" | "Soporte";
		reason: string;
		successTitle: string;
		successMessage: string;
	}) => {
		const orderId = resolveProviderOrderId(order);
		if (!orderId) {
			await showFeedbackModal({
				variant: "error",
				title: "Pedido inválido",
				message: "No se pudo identificar el pedido para actualizar su estado.",
			});
			return;
		}

		try {
			await fetchPatch(`/orders/${orderId}/status`, {
				status,
				reason,
			});
			await refreshProviderOrders();
			await showFeedbackModal({
				variant: "success",
				title: successTitle,
				message: successMessage,
			});
		} catch (error) {
			await showFeedbackModal({
				variant: "error",
				title: "No se pudo actualizar el pedido",
				message:
					error instanceof Error
						? error.message
						: "Ocurrió un error al actualizar el estado del pedido.",
			});
		}
	};

	const handleStartService = async (order: OrderRow) => {
		const confirmation = await showFeedbackModal({
			variant: "warning",
			title: "Iniciar servicio",
			message: "¿Deseas iniciar este servicio? El pedido cambiará a En Progreso.",
			showCancel: true,
			confirmText: "Sí, iniciar",
			cancelText: "Cancelar",
		});

		if (confirmation.action !== "confirm") return;

		await updateProviderOrderStatus({
			order,
			status: "En_Progreso",
			reason: "Servicio iniciado por el proveedor desde Mis Pedidos",
			successTitle: "Servicio iniciado",
			successMessage: "El pedido cambió a En Progreso.",
		});
	};

	const handleCompleteOrder = async (order: OrderRow) => {
		const confirmation = await showFeedbackModal({
			variant: "warning",
			title: "Completar pedido",
			message:
				"¿Deseas marcar este pedido como completado? Esta acción no se puede deshacer.",
			showCancel: true,
			confirmText: "Sí, completar",
			cancelText: "Cancelar",
		});

		if (confirmation.action !== "confirm") return;

		setPendingCompletionOrder(order);
	};

	const handleCompletionImagesSkip = async () => {
		if (!pendingCompletionOrder) return;
		const order = pendingCompletionOrder;
		setIsCompletingWithImages(true);
		try {
			await updateProviderOrderStatus({
				order,
				status: "Completado",
				reason: "Pedido marcado como completado por el proveedor",
				successTitle: "Pedido completado",
				successMessage: "El pedido cambió a Completado.",
			});
		} finally {
			setIsCompletingWithImages(false);
			setPendingCompletionOrder(null);
		}
	};

	const handleCompletionImagesConfirm = async (files: File[]) => {
		if (!pendingCompletionOrder) return;
		const order = pendingCompletionOrder;
		setIsCompletingWithImages(true);
		try {
			if (providerId) {
				try {
					await createPortfolioWithImages(
						{ provider_id: providerId },
						{ pictures_portafolio: files },
					);
				} catch {
					// Portfolio upload failed — proceed with order completion anyway.
				}
			}
			await updateProviderOrderStatus({
				order,
				status: "Completado",
				reason: "Pedido marcado como completado por el proveedor",
				successTitle: "Pedido completado",
				successMessage: "El pedido cambió a Completado.",
			});
		} finally {
			setIsCompletingWithImages(false);
			setPendingCompletionOrder(null);
		}
	};

	const handleReportOrder = async (order: OrderRow) => {
		const reasonModal = await showFeedbackModal({
			variant: "warning",
			title: "Reportar pedido",
			message: "Indica el motivo del reporte para enviarlo a soporte.",
			requireInput: true,
			inputLabel: "Motivo",
			inputPlaceholder: "Describe brevemente el problema",
			showCancel: true,
			confirmText: "Reportar",
			cancelText: "Cancelar",
			validator: (value) => {
				if (!value.trim()) return "Debes indicar un motivo.";
				if (value.trim().length < 5)
					return "El motivo debe tener al menos 5 caracteres.";
				return null;
			},
		});

		if (reasonModal.action !== "confirm" || !reasonModal.value) return;

		await updateProviderOrderStatus({
			order,
			status: "Soporte",
			reason: reasonModal.value.trim(),
			successTitle: "Pedido reportado",
			successMessage: "El pedido cambió a Soporte correctamente.",
		});
	};

	const handleOpenAddService = async () => {
		if (!providerId) return;
		if (!hasLoadedProviderCategories) {
			await showFeedbackModal({
				variant: "info",
				title: "Cargando categorías",
				message: "Espera un momento mientras se cargan tus categorías asignadas.",
				confirmText: "Entendido",
			});
			return;
		}

		let assignedCategoryIds = [...personalCategoryIds];

		if (assignedCategoryIds.length === 0) {
			const pendingCategoryIds = getPendingProviderCategories(providerId) ?? [];

			if (pendingCategoryIds.length > 0) {
				if (!syncingPendingCategoriesRef.current.has(providerId)) {
					syncingPendingCategoriesRef.current.add(providerId);
					try {
						await Promise.all(
							pendingCategoryIds.map((categoryId) =>
								fetchPost("/categorie-provider", {
									category_id: categoryId,
									provider_id: providerId,
								}),
							),
						);
						clearPendingProviderCategories(providerId);
					} catch {
						// If syncing fails now, keep the pending ids for a later retry.
					} finally {
						syncingPendingCategoriesRef.current.delete(providerId);
					}
				}

				assignedCategoryIds = Array.from(
					new Set([...assignedCategoryIds, ...pendingCategoryIds]),
				);
				setPersonalCategoryIds(assignedCategoryIds);
				reloadCategories();
			}

			if (assignedCategoryIds.length === 0) {
				try {
					const latestRelationsPayload = await fetchGet(
						`/categorie-provider/provider/${providerId}`,
					);
					const latestCategoryIds = Array.from(
						new Set(
							extractCategoryProviderRelations(latestRelationsPayload)
								.map((relation) => relation.categories_services?.id)
								.filter(
									(id): id is number =>
										typeof id === "number" && id > 0,
								),
						),
					);

					if (latestCategoryIds.length > 0) {
						assignedCategoryIds = latestCategoryIds;
						setPersonalCategoryIds(latestCategoryIds);
						reloadCategories();
					}
				} catch {
					// Continue to the empty-category warning if the refresh fails.
				}
			}
		}

		const hasAssignedCategories = assignedCategoryIds.length > 0;

		if (!hasAssignedCategories) {
			const result = await showFeedbackModal({
				variant: "warning",
				title: "Necesitas agregar una categoría",
				message:
					"Para crear un servicio primero debes asignar al menos una categoría en Mi Cuenta > Datos personales, en el campo de categorías.",
				showCancel: true,
				confirmText: "Ir a Mi Cuenta",
				cancelText: "Entendido",
			});

			if (result.action === "confirm") {
				setDetailsInitialTab("cuenta");
			}
			return;
		}

		setSelectedService(null);
		setEditingService(null);
		setShowAddService(true);
	};

	return (
		<>
			<Layout>
				<ModalAddService
					isOpen={showAddService}
					categories={effectiveServiceCategories}
					mode={editingService ? "edit" : "create"}
					initialData={
						editingService
							? {
									categoryId:
										categoryMap[
											normalizeLookupKey(editingService.categoria)
										],
									category: editingService.categoria,
									subcategory:
										editingService.subcategorias &&
										editingService.subcategorias.length > 0
											? editingService.subcategorias
											: [editingService.subcategoria],
									name:
										editingService.nombre ??
										editingService.subcategoria,
									price: String(
										parsePriceNumber(editingService.precio),
									),
									warrantyDays: String(editingService.garantia ?? 0),
									estimatedTimeHours:
										editingService.estimatedTimeHours != null
											? String(editingService.estimatedTimeHours)
											: "",
									description: editingService.descripcion ?? "",
									includes: editingService.includes ?? [],
									excludes: editingService.excludes ?? [],
									existingPhotos: editingService.pictures ?? [],
								}
							: undefined
					}
					onClose={() => {
						setShowAddService(false);
						setEditingService(null);
					}}
					onSubmit={(payload) => {
						if (editingService) {
							return handleUpdateService(editingService, {
								categoryId: payload.categoryId,
								category: payload.category,
								subcategory: payload.subcategory,
								name: payload.name,
								price: payload.price,
								warrantyDays: payload.warrantyDays,
								estimatedTimeHours: payload.estimatedTimeHours,
								description: payload.description,
								includes: payload.includes,
								excludes: payload.excludes,
								photos: payload.photos,
							});
						}

						return handleCreateService({
							categoryId: payload.categoryId,
							category: payload.category,
							subcategory: payload.subcategory,
							name: payload.name,
							price: payload.price,
							warrantyDays: payload.warrantyDays,
							estimatedTimeHours: payload.estimatedTimeHours,
							description: payload.description,
							includes: payload.includes,
							excludes: payload.excludes,
							faqs: payload.faqs,
							photos: payload.photos,
						});
					}}
				/>
				{selectedService && (
					<div className='fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 px-0 sm:items-center sm:px-4'>
						<div className='flex h-[92vh] w-full max-h-[92vh] flex-col rounded-t-3xl border border-slate-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-3xl'>
							{/* Header */}
							<div className='flex items-start justify-between border-b border-slate-100 px-4 py-3 sm:px-5 sm:py-4'>
								<div className='inline-flex items-center gap-2 text-sm font-semibold text-slate-700 sm:text-base'>
									<Box className='h-5 w-5 text-primary' />
									<span>Detalles del Servicio</span>
								</div>
								<div className='inline-flex items-center gap-1.5 sm:gap-2'>
									<button
										type='button'
										onClick={() => {
											setEditingService(selectedService);
											setShowAddService(true);
											setSelectedService(null);
										}}
										className='inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-primary/30 px-2.5 py-1.5 text-[11px] font-semibold text-primary transition hover:bg-primary/10 sm:gap-2 sm:px-3 sm:text-xs'
										aria-label='Editar servicio'
									>
										<Pencil className='h-3.5 w-3.5' />
										Editar
									</button>
									<button
										type='button'
										onClick={() => setSelectedService(null)}
										className='cursor-pointer rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600'
										aria-label='Cerrar'
									>
										<X className='h-5 w-5' />
									</button>
								</div>
							</div>

							{/* Scrollable body */}
							<div className='overflow-y-auto overscroll-contain px-4 py-4 sm:px-5 sm:py-5'>
								<div className='space-y-5'>
									{/* Nombre / Servicio */}
									<div>
										<p className='mb-1 text-xs font-medium text-slate-400'>
											Servicio
										</p>
										<p className='text-sm font-semibold text-slate-700'>
											{selectedService.nombre ||
												selectedService.subcategoria}
										</p>
									</div>

									{/* Categoría */}
									<div>
										<p className='mb-1.5 text-xs font-medium text-slate-400'>
											Categoría
										</p>
										<span className='inline-flex items-center rounded-full bg-secondary px-3 py-1 text-xs font-medium text-primary'>
											{selectedService.categoria}
										</span>
									</div>

									{/* Subcategorías */}
									{selectedService.subcategorias &&
										selectedService.subcategorias.length > 0 && (
											<div>
												<p className='mb-1.5 text-xs font-medium text-slate-400'>
													Subcategorías
												</p>
												<div className='flex flex-wrap gap-1.5'>
													{selectedService.subcategorias.map(
														(sub, idx) => (
															<span
																key={idx}
																className='rounded-full border border-slate-200 bg-slate-50 px-3 py-0.5 text-xs text-slate-600'
															>
																{sub}
															</span>
														),
													)}
												</div>
											</div>
										)}

									{/* Info cards */}
									<div className='grid grid-cols-2 gap-3 sm:grid-cols-4'>
										<div className='rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5'>
											<p className='text-xs text-slate-400'>
												Precio base
											</p>
											<p className='text-sm font-semibold text-slate-700'>
												{selectedService.precio}
											</p>
										</div>
										<div className='rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5'>
											<p className='text-xs text-slate-400'>
												Estado
											</p>
											<p className='text-sm font-semibold text-slate-700'>
												{selectedService.activo
													? "Activo"
													: "Inactivo"}
											</p>
										</div>
										<div className='rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5'>
											<p className='text-xs text-slate-400'>
												Tiempo estimado (horas)
											</p>
											<p className='text-sm font-semibold text-slate-700'>
												{selectedService.estimatedTimeHours !=
													null &&
												selectedService.estimatedTimeHours !== ""
													? `${selectedService.estimatedTimeHours} hora(s)`
													: selectedService.duracion != null
														? `${selectedService.duracion} hora(s)`
														: "No especificada"}
											</p>
										</div>
										<div className='rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5'>
											<p className='text-xs text-slate-400'>
												Garantía
											</p>
											<p className='text-sm font-semibold text-slate-700'>
												{selectedService.garantia != null
													? `${selectedService.garantia} día(s)`
													: "No especificada"}
											</p>
										</div>
									</div>

									{/* Descripción */}
									<div>
										<p className='mb-1 text-xs font-medium text-slate-400'>
											Descripción
										</p>
										<p className='text-sm leading-relaxed text-slate-600'>
											{selectedService.descripcion ||
												"Sin descripción disponible para este servicio."}
										</p>
									</div>

									{/* Incluye */}
									{selectedService.includes &&
										selectedService.includes.length > 0 && (
											<div>
												<p className='mb-1.5 text-xs font-medium text-slate-400'>
													Incluye
												</p>
												<ul className='space-y-1'>
													{selectedService.includes.map(
														(item, idx) => (
															<li
																key={idx}
																className='flex items-center gap-2 text-sm text-slate-600'
															>
																<Check className='h-3.5 w-3.5 text-green-500 shrink-0' />
																{item}
															</li>
														),
													)}
												</ul>
											</div>
										)}

									{/* No incluye */}
									{selectedService.excludes &&
										selectedService.excludes.length > 0 && (
											<div>
												<p className='mb-1.5 text-xs font-medium text-slate-400'>
													No incluye
												</p>
												<ul className='space-y-1'>
													{selectedService.excludes.map(
														(item, idx) => (
															<li
																key={idx}
																className='flex items-center gap-2 text-sm text-slate-500'
															>
																<XCircle className='h-3.5 w-3.5 text-red-400 shrink-0' />
																{item}
															</li>
														),
													)}
												</ul>
											</div>
										)}

									{/* Imágenes */}
									{selectedService.pictures &&
									selectedService.pictures.length > 0 ? (
										<div>
											<p className='mb-1.5 text-xs font-medium text-slate-400'>
												Imágenes
											</p>
											<div className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4'>
												{selectedService.pictures.map(
													(pic, idx) => {
														const src = getImageUri(pic);
														return (
															<img
																key={idx}
																src={src}
																alt={`Servicio ${idx + 1}`}
																className='h-24 w-full rounded-xl border border-slate-200 object-cover'
															/>
														);
													},
												)}
											</div>
										</div>
									) : (
										<div className='flex items-center gap-2 rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-400'>
											<ImageIcon className='h-4 w-4' />
											Este servicio no tiene imágenes
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				)}
				{createServiceError && (
					<div className='mt-4 px-4 sm:px-6'>
						<div className='rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 shadow-sm sm:p-4'>
							{createServiceError}
						</div>
					</div>
				)}
				{isLoading ? (
					<div className='mb-6 mt-8 px-4 sm:px-6'>
						<div className='rounded-2xl border border-gray-100 bg-white p-4 text-gray-500 shadow-sm sm:p-6'>
							Cargando perfil...
						</div>
					</div>
				) : error ? (
					<div className='mb-6 mt-8 px-4 sm:px-6'>
						<div className='rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-700 shadow-sm sm:p-6'>
							{error}
						</div>
					</div>
				) : (
					<>
						<ProfesionalCard
							profile={profile ?? undefined}
							onPreview={
								providerId
									? () => navigate(`/proveedor-preview/${providerId}`)
									: undefined
							}
						/>
						<ProfesionalDetails
							profile={personalInfo ?? undefined}
							initialTab={detailsInitialTab}
							usdToBsRate={usdToBsRate}
							badges={obtainedBadges}
							schedule={scheduleRows}
							services={serviceRows}
							faqs={faqRows}
							faqCategories={faqCategories}
							personalCategoryOptions={faqCategories}
							selectedPersonalCategoryIds={personalCategoryIds}
							providerAddresses={providerAddresses}
							paymentMethods={paymentMethods}
							paymentMethodOptions={paymentMethodOptions}
							stats={providerStats}
							requests={providerRequests}
							orders={providerOrders}
							onAcceptSolicitud={handleAcceptRequest}
							onRejectSolicitud={handleRejectRequest}
							onToggleServiceStatus={handleToggleServiceStatus}
							togglingServiceId={togglingServiceId}
							onCreateFaq={handleCreateProviderFaq}
							onUpdateFaq={handleUpdateProviderFaq}
							onDeleteFaq={handleDeleteProviderFaq}
							isSavingFaq={isSavingFaq}
							onCreateProviderAddress={handleCreateProviderAddress}
							onUpdateProviderAddress={handleUpdateProviderAddress}
							onToggleProviderAddress={handleToggleProviderAddress}
							onCreatePaymentMethod={handleCreatePaymentMethod}
							onUpdatePaymentMethod={handleUpdatePaymentMethod}
							onDeletePaymentMethod={handleDeletePaymentMethod}
							isSavingAddress={isSavingAddress}
							isSavingPaymentMethod={isSavingPaymentMethod}
							onAddService={() => {
								void handleOpenAddService();
							}}
							disableAddService={isCreatingService || !providerId}
							onEditService={(service) => {
								setSelectedService(null);
								setEditingService(service);
								setShowAddService(true);
							}}
							onViewService={(service) => {
								setSelectedService(service);
							}}
							onSavePersonalInfo={handleSavePersonalInfo}
							onSaveSchedule={handleSaveSchedule}
							isSavingPersonalInfo={isSavingPersonalInfo}
							isSavingSchedule={isSavingSchedule}
							onCotizar={handleStartQuotation}
							onStartService={handleStartService}
							onCompleteOrder={handleCompleteOrder}
							onReportOrder={handleReportOrder}
						/>
					</>
				)}
			</Layout>
			<ModalImagenesCompletado
				open={pendingCompletionOrder !== null}
				isLoading={isCompletingWithImages}
				onSkip={() => {
					void handleCompletionImagesSkip();
				}}
				onConfirm={(files) => {
					void handleCompletionImagesConfirm(files);
				}}
			/>
			{feedbackModalConfig
				? (() => {
						const variant = feedbackModalConfig.variant ?? "info";
						const { Icon, iconClass, badgeClass } =
							MODAL_VARIANT_STYLES[variant];
						const confirmLabel =
							feedbackModalConfig.confirmText ?? "Entendido";
						const cancelLabel = feedbackModalConfig.cancelText ?? "Cancelar";
						const showCancelButton = feedbackModalConfig.showCancel ?? false;
						const confirmDisabled = Boolean(
							feedbackModalConfig.requireInput &&
							!feedbackModalInput.trim(),
						);
						const confirmButtonClass =
							variant === "error"
								? "bg-rose-600 hover:bg-rose-500"
								: variant === "success"
									? "bg-emerald-600 hover:bg-emerald-500"
									: variant === "warning"
										? "bg-amber-500 hover:bg-amber-400"
										: "bg-[#008db1] hover:bg-[#007491]";
						return (
							<div
								className='fixed inset-0 z-60 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm'
								onMouseDown={(event) => {
									if (event.target === event.currentTarget) {
										handleFeedbackModalCancel();
									}
								}}
							>
								<div
									role='dialog'
									aria-modal='true'
									className='relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl'
								>
									<button
										type='button'
										onClick={handleFeedbackModalCancel}
										className='absolute right-4 top-4 rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600'
										aria-label='Cerrar'
									>
										<X className='h-4 w-4' />
									</button>
									<div className='flex items-start gap-4 pr-6'>
										<div
											className={`flex h-12 w-12 items-center justify-center rounded-full ${badgeClass}`}
										>
											<Icon className={`h-6 w-6 ${iconClass}`} />
										</div>
										<div className='flex-1'>
											<h3 className='text-lg font-semibold text-slate-900'>
												{feedbackModalConfig.title}
											</h3>
											{feedbackModalConfig.message ? (
												<p className='mt-2 text-sm leading-relaxed text-slate-600'>
													{feedbackModalConfig.message}
												</p>
											) : null}
										</div>
									</div>
									{feedbackModalConfig.requireInput ? (
										<div className='mt-5'>
											{feedbackModalConfig.inputLabel ? (
												<label className='mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500'>
													{feedbackModalConfig.inputLabel}
												</label>
											) : null}
											<textarea
												rows={4}
												value={feedbackModalInput}
												onChange={(event) => {
													setFeedbackModalInput(
														event.target.value,
													);
													setFeedbackModalError(null);
												}}
												placeholder={
													feedbackModalConfig.inputPlaceholder
												}
												className='w-full resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'
											/>
											{feedbackModalError ? (
												<p className='mt-2 text-xs text-rose-600'>
													{feedbackModalError}
												</p>
											) : null}
										</div>
									) : null}
									<div className='mt-6 flex items-center justify-end gap-3'>
										{showCancelButton ? (
											<button
												type='button'
												onClick={handleFeedbackModalCancel}
												className='rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50'
											>
												{cancelLabel}
											</button>
										) : null}
										<button
											type='button'
											onClick={handleFeedbackModalConfirm}
											disabled={confirmDisabled}
											className={`rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-60 ${confirmButtonClass}`}
										>
											{confirmLabel}
										</button>
									</div>
								</div>
							</div>
						);
					})()
				: null}
		</>
	);
};
